import React, { useState, useEffect, useMemo, useRef, useId } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { FaRegClock,FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import PreTradeChecklistModal from "./PreTradeChecklistModal";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore"; 
import { db } from "../firebase";
import SimpleCalculator from './SimpleCalculator'; 
import { FOREX_PAIRS, FOREX_INDICES, isIndexSymbol } from "../constants/instruments";

// --- Icons ---
const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2" />
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

const IconJournal = () => (
  <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="#f59e42" strokeWidth="2" />
      <path d="M8 2v4M16 2v4" stroke="#f59e42" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

// --- Dropdowns ---
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Sydney","Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell", "CounterTrend - Buy","CounterTrend - Sell", "Did Not Take"];
const TIMEFRAME_OPTIONS_FX = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];


// --- Products (Forex + Metals) ---
 // Build one menu (pairs + indices + your metals from this file)
 const SYMBOL_OPTIONS = [...FOREX_PAIRS, ...FOREX_INDICES
 ];



// --- Defaults ---
const defaultSettings = {
  Symbol: "",
  "Initial Balance": 2000,
  "Max Lot Size": 20,
  "Stop Loss": 30,
  "Daily Goal": 500,
  "Min Lot Size": 0.01,
  "Take Profit": 60,
  "Daily Loss Limit": 200,
  "Account Goal": 10000,
  "Running Balance": 2000,
  "Risk Percentage": 3.00,
  "Leverage": 100,
  "Account Currency": "USD",
  "Pip Value": 10, // Default for standard lot major pairs (will auto-calc)
  "Number of Trades to Goal": 0,
  "Max Loss Limit": 0,
  "Maximum Loss Limit": 0, 
"MaxLossLimitBlownShownDate": "",
"Risk Mode": "%",   // "%” or “$”
"Risk Dollar": 0,
  DailyGoalPopupShownDate: "",
  LossLimitWarningShownDate: "",
  MoodVolatilityPopupShownDate: "",
  OverRiskWarningShownDate: "",
  ConsecutiveWinsPopupShownDate: "",
  AccountGoalReachedShownDate: "",
  LessonsLearnedStreakWarningShownDate: "",
  RiskOfRuinWarningShownDate: "",
  TwoLossWarningShownDate: "",
  ThreeLossWarningShownDate: "",
  OvertradingPopupShownDate: "",
  TwoLossAfterGoalWarningShownDate: "",
  Win10NoRulesPopupShownDate: "",
  Win10WithRulesPopupShownDate: "",
  PretradeChecklistShownDate: "",
  "DrawdownLockActive": false,
  "DrawdownLockStartedDate": "",
  "DrawdownLockPeak": 0,
  "Calculator Only": false,
};

const defaultJournal = {
  "Actual Balance": "",
  "Position": "",
  "Date": getTodayISO(),
  "Session": "",
  "Entry Time": "",
  "Exit Time": "",
  "Entry Price": "",
  "Exit Price": "",
  "Did I follow my Rules?": "",
  "Win": false,
  "Loss": false,
  "Mood_Mindset": "",
  "Grade": "",
  "Size": "",
  "Pips":"",
  "Suggested Max # of Contracts": 0, // Futures only
  "Notes": "",
  "Emotional Notes": "",
  "Links_Screenshots": "",
  "Lesson": "",
  "LogLesson": false,
  "Stop Loss_Take Profit": "",
  "IdealSL":"",
  "Percent of Balance": "",
  "Entry Strategy": "",
  "Exit Strategy": "",
  "Actual Risk to Reward": "",
  "Duration": "",
  "Trade #": "",
  "Product": "",
  "Actual Percent Growth": "",
  "Actual Reward": "",
  "Potential": "",
  "Timeframe": "",
  "Target Reached": false
};

// Popups that should show at most once per local day
const DAILY_SCOPED_IDS = new Set([
  "twoLossAfterGoalWarning",
  "twoLossWarning",
  "threeLossWarning",
  "dailyLossLimitExceeded",
  "dailyGoalPopup",
  "consecutiveWinsPopup",
  "overtradingPopup",
  "tooManyLossesPopup",
  "moodVolatilityPopup",
  "lessonsLearnedStreakWarning",
  "riskOfRuinWarning",
  "accountGoalReached",
  "maxLossLimitBlown",
  "drawdownWarning",
  "win10NoRulesPopup",
  "win10WithRulesPopup"
]);


// Use the same constant in each file
const TWO_LOSS_MESSAGE = [
  "You've had 2 losses in a row.",
  "",
  "Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?",
  "",
  "Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.",
  "",
  "Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.",
  "",
  "You've got this. Apply what you learned in the next one!"
].join("\n");


// Treat these keys as "clearable numeric" (keep raw string while typing)
const RISK_KEYS = new Set(["Risk Percentage", "Risk Dollar", "Risk Amount", "Risk Dollar Amount"]);

// Helpers for blur-commit
const isDec = (s) => s === "" || /^\d*\.?\d*$/.test(s);
const commitNumberOrEmpty  = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) ? n : "";
};
// For $ risk: must be > 0, but no upper limit
const commitPositiveOrEmpty = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : "";
};

function getSessionFromTime(time24) {
  if (!time24) return "";
  
  const [hour] = time24.split(":").map(Number);

  // New York: 8:00 AM - 5:00 PM (17:00)
  if (hour >= 8 && hour < 17) return "New York";
  
  // London: 3:00 AM - 8:00 AM
  if (hour >= 3 && hour < 8) return "London";
  
  // Asian (Tokyo): 7:00 PM (19:00) - 3:00 AM 
  // (Using || because it crosses midnight)
  if (hour >= 19 || hour < 3) return "Asian";
  
  // Sydney: 5:00 PM (17:00) - 7:00 PM (19:00)
  // (Captures the gap after NY closes and before Tokyo opens)
  if (hour >= 17 && hour < 19) return "Sydney";
  
  return "";
}

// --- Helper Functions ---
function getPipValue(symbol, lots = 1, accountCurrency = "USD") {
  const sym = (symbol || "").toUpperCase();

  // Metals (unchanged)
  if (sym.startsWith("XAU")) return 1 * lots;   // $1 per pip per 1.00 lot
  if (sym.startsWith("XAG")) return 5 * lots;

  // Index CFDs – default $1 per point per 1.00 lot (typical; overrideable in UI)
  const INDEX_PIP_VALUE = {
    US30: 1, NAS100: 1, SPX500: 1, US2000: 1,
    GER40: 1, UK100: 1, FRA40: 1, JP225: 1, HK50: 1, AUS200: 1
  };
  if (INDEX_PIP_VALUE[sym] != null) return INDEX_PIP_VALUE[sym] * lots;

  // Currencies
  if (sym.includes("JPY")) return 9.09 * lots;  // approx for USD/JPY @ $9.09/lot/pip
  return 10 * lots;                              // $10 per pip per 1.00 lot
}

function getPipValuePerLot(symbol) {
  const s = String(symbol || "").toUpperCase();

  // Metals (typical values; adjust to your broker if needed)
  if (s.startsWith("XAU")) return 1;   // $1 per "pip"/point per 1.00 lot
  if (s.startsWith("XAG")) return 5;   // $5 per "pip"/point per 1.00 lot

  // Indices (CFDs): start with $1 per point per 1.00 lot (easy to tune later per symbol)
  if (isIndexSymbol(s)) return 1;

  // FX
  if (s.endsWith("JPY")) return 9.09;  // ≈ $9.09 per pip per 1.00 lot for JPY quotes
  return 10;                           // majors ≈ $10 per pip per 1.00 lot
}

function getPipSizeForSymbol(symbol) {
  if (!symbol) return 0.0001;
  const s = String(symbol).toUpperCase().trim();

  // 1. Indices (CFDs) -> 1 point = 1 unit
  const indexSymbols = ["US30", "NAS100", "SPX500", "US2000", "GER40", "UK100", "FRA40", "JP225", "HK50", "AUS200"];
  if (indexSymbols.some(idx => s.includes(idx)) || (typeof isIndexSymbol === 'function' && isIndexSymbol(s))) {
      return 1;
  }

  // 2. Metals & JPY Pairs -> 0.01 per pip
  if (s.includes("JPY") || s.includes("XAU") || s.includes("GOLD") || s.includes("XAG") || s.includes("SILVER")) {
      return 0.01;
  }

  // 3. Standard Forex Pairs -> 0.0001 per pip
  return 0.0001;
}

function getEffectiveDistance(s, field) {
  const mode = s[`${field} Mode`] || "Pips";
  const val = Number(s[field] || 0);
  const entry = Number(s["Entry Price"] || 0);
  
  // Check for "Symbol" (Backtest) OR "Product" (Retail)
  const product = s["Symbol"] || s["Product"] || ""; 

  if (!val) return 0;

  if (mode === "Price" && entry > 0) {
      const pipSize = getPipSizeForSymbol(product);
      if (pipSize > 0) {
          return Math.abs(val - entry) / pipSize;
      }
  }

  if (mode === "Ticks") {
      const isIdx = getPipSizeForSymbol(product) === 1;
      return isIdx ? val / 100 : val / 10;
  }

  return val; // Mode is "Pips"
}

function computePipsFromPricesAbs(symbol, entryPrice, exitPrice) {
  if (entryPrice == null || exitPrice == null) return null;

  const entryStr = String(entryPrice).trim();
  const exitStr  = String(exitPrice).trim();
  if (entryStr === "" || exitStr === "") return null;

  const entry = Number(entryStr);
  const exit  = Number(exitStr);
  if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;

  const diff = Math.abs(exit - entry);
  const pipSize = getPipSizeForSymbol(symbol) || 0.0001;

  const rawPips = diff / pipSize;
  if (!Number.isFinite(rawPips)) return null;

  // Round to 2 decimals to support fractional points cleanly
  return Math.round(rawPips * 100) / 100;
}



function getTodayISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getTodayLocalISO() {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}


function useBlockWheelOnNumber(inputRef) {
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const stopWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", stopWheel, { passive: false });
    return () => el.removeEventListener("wheel", stopWheel);
  }, [inputRef]);
}

function getSuggestedLotSize(balance, riskPercent, stopLoss, pipValue, minLot, maxLot) {
  const riskDollar = (Number(balance) * Number(riskPercent)) / 100;
  const perLotRisk = Number(stopLoss) * Number(pipValue);
  let rawLots = perLotRisk > 0 ? riskDollar / perLotRisk : 0;
  let lots = Math.floor(rawLots * 100) / 100; // round down to 0.01
  if (lots > maxLot) lots = maxLot;
  if (lots < minLot) lots = 0; // not possible to size safely
  return Number(lots.toFixed(2));
}

function formatCurrency(val, currency = "USD") {
  if (val === "" || val === null || typeof val === "undefined" || isNaN(val)) return "";
  return Number(val).toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2
  });
}

function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>{value}</span>
    </div>
  );
}

// --- Time helpers ---
function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str;
  const ampm = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (ampm) {
    let [h, m, ap] = ampm;
    h = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${m}`;
  }
  return "";
}
function toAmPm(str) {
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}
function parseTimeToParts(str) {
  if (!str) return null;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return { h, m, s: s || 0 };
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [h, m, ap] = match;
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return { h, m, s: 0 };
  }
  return null;
}
function getDurationHHMMSS(start, end) {
  const et = parseTimeToParts(start);
  const xt = parseTimeToParts(end);
  if (!et || !xt) return "";
  let startSec = et.h * 3600 + et.m * 60 + (et.s || 0);
  let endSec = xt.h * 3600 + xt.m * 60 + (xt.s || 0);
  let diff = endSec - startSec;
  if (diff < 0) diff += 24 * 3600;
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// === Risk Toggle Helpers ===
function getRiskMode(s) {
  return String(s?.["Risk Mode"] || "%") === "$" ? "$" : "%";
}
function riskBudgetDollar(s, balanceMaybe) {
  const mode = getRiskMode(s);
  const bal  = Number(balanceMaybe ?? s?.["Running Balance"] ?? 0);
  if (mode === "$") {
    const dollar =
      s?.["Risk Dollar"] ?? s?.["Risk Amount"] ?? s?.["Risk Dollar Amount"] ?? 0;
    return Math.max(0, Number(dollar));
  }
  return Math.max(0, bal * Number(s?.["Risk Percentage"] || 0) / 100);
}
function riskPercentFromDollar(s, balanceMaybe) {
  const bal    = Number(balanceMaybe ?? s?.["Running Balance"] ?? 0);
  const dollar = riskBudgetDollar(s, bal);
  if (bal <= 0) return 0;
  return (dollar / bal) * 100;
}


function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">
                On Tilt
              </div>
              <p className="mt-5  text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5  text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>

              <p className="mt-6 text-[14px] font-bold leading-relaxed">
                What to do:  
              </p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>

            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}


function Tooltip({ children, content, variant = "neutral" }) {
  const id = useId();
  const [open, setOpen] = useState(false);

  const palette = {
    neutral: {
      panel: "bg-gray-900/95 text-white ring-white/10",
      arrow: "bg-gray-900/95",
      focus: "focus:ring-yellow-500", // trigger outline color
    },
    yellow: {
      panel: "bg-yellow-600/95 text-white ring-yellow-300/30",
      arrow: "bg-yellow-600/95",
      focus: "focus:ring-yellow-500",
    },
    red: {
      panel: "bg-red-800/95 text-white ring-red-300/30",
      arrow: "bg-red-800/95",
      focus: "focus:ring-red-500",
    },
    blue: {
      panel: "bg-slate-900/95 text-white ring-blue-300/30",
      arrow: "bg-slate-900/95",
      focus: "focus:ring-blue-500",
    },
  }[variant] || {
    panel: "bg-gray-900/95 text-white ring-white/10",
    arrow: "bg-gray-900/95",
    focus: "focus:ring-yellow-500",
  };

  return (
    <span
      className="relative inline-flex items-center group"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Trigger (focusable + tap/click for mobile) */}
      <span
        tabIndex={0}
        aria-describedby={id}
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        className={`underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}
      >
        {children}
      </span>

      {/* Tooltip panel */}
      <span
        id={id}
        role="tooltip"
        className={`
          select-text absolute z-[60]
          bottom-full left-1/2 -translate-x-1/2 mb-2
          max-w-[min(90vw,28rem)] w-max break-words
          rounded-lg px-4 py-3 text-[13px] leading-relaxed font-normal
          shadow-2xl ring-1 backdrop-blur-sm
          transition opacity-0 scale-95 pointer-events-none
          ${palette.panel}
          ${open ? "opacity-100 scale-100 pointer-events-auto" : ""}
          group-hover:opacity-100 group-hover:scale-100
          group-focus-within:opacity-100 group-focus-within:scale-100
        `}
      >
        {/* Allow string or JSX for content */}
        {typeof content === "string" ? <span className="block">{content}</span> : content}

        {/* Arrow */}
        <span
          aria-hidden="true"
          className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow`}
        />
      </span>
    </span>
  );
}

// --- DEBUG HELPERS (paste near top once) ---
const FXDBG = (...args) => {
  try {
    console.log(
      "%c[ForexDebug]",
      "color:#2563eb;font-weight:bold",
      new Date().toLocaleTimeString(),
      ...args
    );
  } catch (e) {
    // no-op
  }
};
// JSON-safe dumper (avoids circular structures)
const FXDUMP = (label, obj) => {
  try {
    console.log(
      "%c[ForexDebug] " + label,
      "color:#7c3aed",
      JSON.parse(JSON.stringify(obj))
    );
  } catch (e) {
    console.log("%c[ForexDebug] " + label, "color:#7c3aed", obj);
  }
};


// === Component ===
export default function ForexCalculateForm({ userId, accountId }) {
  const [settings, setSettings] = useState({ ...defaultSettings });
  const [journal, setJournal] = useState({ ...defaultJournal });
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTrade, setSavingTrade] = useState(false);
  const [calculatedSummary, setCalculatedSummary] = useState(null);

  // projections & warnings
  const [winProjection, setWinProjection] = useState({ trades: [], tradeCount: 0 });
  const [lossProjection, setLossProjection] = useState({ trades: [], tradeCount: 0, suggestedLots: 0 });
  const [showRiskWarningPopup, setShowRiskWarningPopup] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [entryStrategies, setEntryStrategies] = useState([]);
  const [exitStrategies, setExitStrategies] = useState([]);
  const [postSaveCheckInfo, setPostSaveCheckInfo] = useState(null);
  const popupsAllowedRef = useRef(false);
  const assetClass = "Forex";
  // Daily Progress objects used by the “Amounts” bars
  const [dailyGoalProg, setDailyGoalProg] = useState({
    done: 0,
    target: Number(settings["Daily Goal"] || 0),
    pct: 0,
  });

  const [dailyLossProg, setDailyLossProg] = useState({
    lost: 0,
    limit: Number(settings["Daily Loss Limit"] || 0),
    pct: 0,
  });

  // Only if you still reference this anywhere in JSX/logic:
  const [dailyLossBeyondWins, setDailyLossBeyondWins] = useState(0);
  const [showCalculator, setShowCalculator] = useState(false);
  const [userPrefs, setUserPrefs] = useState(() => {
    // Initialize synchronously from localStorage to prevent balance flashing
    const cached = localStorage.getItem("isPrivacyMode");
    return cached === "true" ? { "Privacy Mode": true } : {};
  });
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const [userFavorites, setUserFavorites] = useState({ futures: [], forex: [] });
  const [subStatus, setSubStatus] = useState("");

  useEffect(() => {
    if (!userId) return;
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSubStatus(snapshot.docs[0].data().status || "");
      } else {
        setSubStatus("");
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const isTrialing = subStatus === "TRIALING";

  useEffect(() => {
    if (!userId) return;

    const ref = doc(db, "users", userId, "settings", "userSettings");
    const unsub = onSnapshot(ref, snap => {
      const favs = snap.exists() ? (snap.data().favorites || { futures: [], forex: [] }) 
                                 : { futures: [], forex: [] };
      setUserFavorites(favs);
    });

    return unsub; // cleanup
  }, [userId]);

  const favoriteForexOptions = useMemo(() => {
    const fav = (userFavorites?.forex || []).map(s => s.toUpperCase());
    if (!fav.length) return [];
    return fav
      .map(sym => SYMBOL_OPTIONS.find(([v]) => String(v).toUpperCase() === sym))
      .filter(Boolean);
  }, [userFavorites, SYMBOL_OPTIONS]);

  const favoriteSymbolValues = useMemo(
    () => new Set(favoriteForexOptions.map(([v]) => v)),
    [favoriteForexOptions]
  );

  const nonFavoriteSymbols = useMemo(
    () => SYMBOL_OPTIONS.filter(([v]) => !favoriteSymbolValues.has(v)),
    [favoriteSymbolValues]
  );

  function toPips(symbol, raw) {
    const n = Number(raw) || 0;
    const isJpy = (symbol || "").toUpperCase().includes("JPY");
    if (!isJpy) return n;                   // non-JPY: already pips

    // Heuristic: if the number is small and has a decimal, it's likely price distance
    if (n > 0 && n < 5 && String(raw).includes(".")) {
      return Math.round(n / 0.01);          // e.g., 0.30 → 30 pips
    }
    return n;                               // already pips (e.g., 20, 30)
  }

  useEffect(() => {
  setDailyGoalProg(prev => ({ ...prev, target: Number(settings["Daily Goal"] || 0) }));
  setDailyLossProg(prev => ({ ...prev, limit: Number(settings["Daily Loss Limit"] || 0) }));
}, [settings["Daily Goal"], settings["Daily Loss Limit"]]);

  useEffect(() => {
  if (!userId || !accountId) return;
  updateDailyProgress(); // fetches rows internally
}, [userId, accountId]);

// Attach a few helpers to window so you can test from the browser console.
useEffect(() => {
  if (typeof window !== "undefined") {
    window.__fx = {
      settings: () => ({ ...settings }),
      queue: () => ({ queue: popupQueue, current: currentPopup }),
      postSave: () => postSaveCheckInfo,
      // Force-run the check with current settings
      testBlown: async () => {
        FXDBG("window.__fx.testBlown() called");
        popupsAllowedRef.current = true;
        await checkMaxLossLimitBlown(settings, updateSettings, userId, accountId, setSettings);
        FXDBG("window.__fx.testBlown() finished");
      }
    };

  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  setJournal(prev => {
    const next = { ...prev };
    let changed = false;

    // Helper to apply default if field is empty
    const applyDefault = (field, settingKey) => {
      const defVal = String(settings[settingKey] || "").trim();
      if (defVal && !String(next[field] || "").trim()) {
        next[field] = defVal;
        changed = true;
      }
    };

    applyDefault("Entry Strategy", "Default Entry Strategy");
    applyDefault("Exit Strategy", "Default Exit Strategy");
    applyDefault("Timeframe", "Default Timeframe");
    applyDefault("Session", "Default Session"); // ✅ ADDED THIS LINE

    return changed ? next : prev;
  });
}, [
  settings["Default Entry Strategy"], 
  settings["Default Exit Strategy"], 
  settings["Default Timeframe"],
  settings["Default Session"] // ✅ ADDED THIS DEPENDENCY
]);

useEffect(() => {
  setJournal(prev => {
    // If user already has a value (or editing an existing trade), do not override.
    const cur = String(prev["Timeframe"] || "").trim();
    if (cur) return prev;

    const defTf = String(settings?.["Default Timeframe"] || "").trim();
    return defTf ? { ...prev, "Timeframe": defTf } : prev;
  });
}, [settings?.["Default Timeframe"]]); 

// --- Auto-sync Size, Entry, and Exit Prices to Journal (FOREX ONLY) ---
useEffect(() => {
    const entryPrice = Number(settings["Entry Price"] || 0);
    const tpVal = Number(settings["Take Profit"] || 0);
    const position = journal["Position"]; 
    let estTargetPrice = "";
    
    if (entryPrice > 0 && tpVal > 0) {
        if (settings["Take Profit Mode"] === "Price") {
            // Use the actual target price if that's what the user typed
            estTargetPrice = tpVal;
        } else {
            // Calculate the target price if they typed a distance
            const pipSize = getPipSizeForSymbol(settings["Symbol"]) || 0.0001;
            const isIdx = isIndexSymbol(settings["Symbol"]);
            let ptsDist = tpVal;
            
            if (settings["Take Profit Mode"] === "Ticks") {
                ptsDist = isIdx ? tpVal / 100 : tpVal / 10;
            }
            
            if (position === "Sell") {
                estTargetPrice = Number((entryPrice - ptsDist * pipSize).toFixed(isIdx ? 2 : 5));
            } else {
                // Default to Buy (adding distance)
                estTargetPrice = Number((entryPrice + ptsDist * pipSize).toFixed(isIdx ? 2 : 5));
            }
        }
    }

    setJournal(prev => {
        const next = { ...prev };
        let changed = false;

        // 1. Auto-sync the Suggested Lot Size
        if (calculatedSummary && calculatedSummary.lots !== undefined) {
          if (prev["Size"] === undefined || prev["Size"] === "") {
            next["Size"] = calculatedSummary.lots;
            changed = true;
          }
        }
        
        // 2. Auto-sync Entry Price
        if (settings["Entry Price"] !== undefined && settings["Entry Price"] !== "") {
            if (next["Entry Price"] !== settings["Entry Price"]) {
                next["Entry Price"] = settings["Entry Price"];
                changed = true;
            }
        }
        
        // 3. Auto-sync Exit Price
        if (estTargetPrice !== "") {
            if (next["Exit Price"] !== estTargetPrice) {
                next["Exit Price"] = estTargetPrice;
                changed = true;
            }
        }
        
        return changed ? next : prev;
    });
}, [settings["Entry Price"], settings["Take Profit"], settings["Take Profit Mode"], settings["Symbol"], calculatedSummary, journal["Position"]]);


  // UI state
  const [missingFields, setMissingFields] = useState([]);
  const [projectionOpen, setProjectionOpen] = useState(false);
  const [lossProjectionOpen, setLossProjectionOpen] = useState(false);
  const [showDaySeparators, setShowDaySeparators] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [showTradeSaved, setShowTradeSaved] = useState(false);
  // NEW — daily progress state
const [dailyProfit, setDailyProfit] = useState(0);     // today's net profit (wins - losses), clamped at 0
const [dailyLossAbs, setDailyLossAbs] = useState(0);   // today's realized losses (absolute)
// Keeps 'id -> yyyy-mm-dd' for this session
const shownTodayRef = useRef({});

  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const { width, height } = useWindowSize();
  const hasRunInitialAutoCalc = useRef(false);
  const loadedFromServer = useRef(false);
  const lotSizeRef = useRef(null);



  // derived
  const stopLoss = Number(settings["Stop Loss"]);
  const riskPercent = Number(settings["Risk Percentage"]);
  const runningBalance = Number(settings["Running Balance"]);
  const minLot = Number(settings["Min Lot Size"]) || 0.01;
  const maxLot = Number(settings["Max Lot Size"]) || 100;
  const leverage = Number(settings["Leverage"]) || 100;
  const pipValue = Number(settings["Pip Value"]) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);
  const riskDollar = runningBalance * riskPercent / 100;

  // Popup Queue States
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null); // { id: 'popupName', data: {...} }
  const [showChecklist, setShowChecklist] = useState(false);
  const dailyGoalAmount      = Number(settings["Daily Goal"] || 0);
  const dailyLossLimitAmount = Number(settings["Daily Loss Limit"] || 0);
  const displayBalance = Number(settings["Running Balance"] || 0);
  const displayGoal    = Number(settings["Account Goal"]   || 0);

// Calc basis (what you use for lot sizing/compounding math)
// In the regular forex variant, it's the same as display.
const calcBalance = displayBalance;
const calcGoal    = displayGoal;

// Always use local day// Always use local day
const todayLocalISO = () =>
  (typeof getTodayLocalISO === "function")
    ? getTodayLocalISO()
    : new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD


// Has this popup been shown today?
const wasShownToday = (settings, key) => (settings?.[key] || "") === todayLocalISO();

// Mark popup as shown *today* in Firestore and local state
async function markShownToday(key, updateSettingsFunc, userId, accountId, setLocalSettings) {
  const today = todayLocalISO();           // uses the local-day helper shown below
  const patch = { [key]: today };          // ✅ use the function's 'key' parameter
  await updateSettingsFunc(userId, accountId, patch);
  setLocalSettings(prev => ({ ...prev, ...patch }));
}





// (Optional) clear yesterday’s markers; never clear *today* here
async function clearIfOld(key, settings, updateSettingsFunc, userId, accountId, setLocalSettings) {
  const today = todayLocalISO();
  if (settings?.[key] && settings[key] !== today) {
    const patch = { [key]: "" };
    await updateSettingsFunc(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
  }
}


  useEffect(() => {
    if (!userId || !accountId) return;
    let alive = true;

    (async () => {
      const rows = await fetchJournalRows(userId, accountId);
      const todayISO = getTodayLocalISO();
      const todays = (rows || []).filter(r => (r?.["Date"] || "").startsWith(todayISO));

      // Net P&L for today (wins - losses)
      const net = todays.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);

      const goal      = Number(settings["Daily Goal"] || 0);
      const lossLimit = Number(settings["Daily Loss Limit"] || 0);

      // What the bars show
      const netPositive      = Math.max(0, net);   // for Daily Goal
      const lossesBeyondWins = Math.max(0, -net);  // for Daily Loss Limit

      // Cap the *displayed* amounts and percentages
      const doneCapped = netPositive;  
      const pctGoal = goal > 0 ? (netPositive / goal) * 100 : 0;

      const lostCapped = lossesBeyondWins;  
      const pctLoss = lossLimit > 0 ? (lossesBeyondWins / lossLimit) * 100 : 0;

      if (!alive) return;
      setDailyGoalProg({ done: doneCapped, target: goal, pct: pctGoal });
      setDailyLossProg({ lost: lostCapped, limit: lossLimit, pct: pctLoss });
    })();

    return () => { alive = false; };
  }, [userId, accountId, settings["Daily Goal"], settings["Daily Loss Limit"]]);



useEffect(() => {
  // Inputs
  const rb       = Number(settings["Running Balance"] || 0);
  const goal     = Number(settings["Account Goal"] || 0);
  const rp       = Number(settings["Risk Percentage"] || 0);
  const sl       = Number(settings["Stop Loss"] || 0);
  const tp       = Number(settings["Take Profit"] || 0);
  const minLot   = Number(settings["Min Lot Size"] || 0.01);
  const maxLot   = Number(settings["Max Lot Size"] || 100);
  const pipValue = Number(settings["Pip Value"]) ||
                   getPipValue(settings["Symbol"], 1, settings["Account Currency"]);
  const riskMode   = getRiskMode(settings);                  // "%" or "$"
  const riskDollar = Number(settings["Risk Dollar"] || 0);   // constant $/trade

  // Rebuild the tables
  setWinProjection(
    getCompoundingProjection({
      balance: rb,
      goal,
      riskPercent: rp,
      stopLoss: sl,
      takeProfit: tp,
      minLot,
      maxLot,
      pipValue,
      riskMode, riskDollar
    })
  );

  setLossProjection(
    getLossProjection({
      balance: rb,
      stopLoss: sl,
      pipValue,
      minLot,
      maxLot,
      riskPercent: rp,
      riskMode, riskDollar
    })
  );
}, [
  settings["Running Balance"],
  settings["Account Goal"],
  settings["Risk Percentage"],
  settings["Stop Loss"],
  settings["Take Profit"],
  settings["Min Lot Size"],
  settings["Max Lot Size"],
  settings["Pip Value"],
  settings["Symbol"],
  settings["Risk Mode"],
settings["Risk Dollar"],
]);



  // Daily Goal: show only today's net positive P&L
   // If the account goal is reached, show Running Balance in the left amount;
   // otherwise show today's net profit. Also, don't cap the % so it can exceed 100%.
   const runningBal  = Number(settings["Running Balance"] || 0);
   const accountGoal = Number(settings["Account Goal"] || 0);
   const showRB      = accountGoal > 0 && runningBal >= accountGoal;
   //const goalDoneAmt = showRB ? runningBal : positivePnL;
  

// Optional: set your preferred time zone here (or pass one in when calling).
// For US markets you might prefer: 'America/New_York'
// Leave as undefined to use the user's local time.
const DEFAULT_TIME_ZONE = undefined;

function getDailyGoalMessage(timeZone = DEFAULT_TIME_ZONE) {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    ...(timeZone ? { timeZone } : {})
  }).format(now);

  if (weekday === 'Sunday' || weekday === 'Monday') {
    return "You are off to a great start of the week. Keep the momentum, consider stopping for the day and enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Tuesday' || weekday === 'Wednesday') {
    return "You have the rest of the week!! The market will always be there, but this moment in time will be gone forever. Don't forget to enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Thursday') {
    return "Go and enjoy the rest of your day!";
  }
  if (weekday === 'Friday') {
    return "Today is Capital Preservation Friday! Consider stopping for the day and get ready for a great weekend!!";
  }
  // Saturday or anything unexpected — fallback
  return "Go and enjoy the rest of your day!";
}


// Checks if today's net loss (after wins) has hit the Daily Loss Limit.
  async function checkDailyLossLimit(trades, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
    try {
      const today = getTodayLocalISO();

      const lossLimit = Number(currentSettings["Daily Loss Limit"] || 0);
      if (lossLimit <= 0) return;

      const todays = (trades || []).filter(r => (r["Date"] || "").startsWith(today));
      const net = todays.reduce((sum, r) => sum + Number(r["Actual Reward"] || 0), 0);
      const netLossAbs = Math.max(0, -net);

      const shownDateKey  = "DailyLossLimitPopupShownDate";
      const shownLimitKey = "DailyLossLimitPopupShownForLimit";

      if (netLossAbs >= lossLimit && currentSettings[shownDateKey] !== today) {
        // ✅ FIX: Changed `lossForDay` to `lostBeyondWins` to match the popup's code
        queuePopup("dailyLossLimitExceeded", { limit: lossLimit, lostBeyondWins: netLossAbs });

        await updateSettingsFunc(userId, accountId, {
          [shownDateKey]: today,
          [shownLimitKey]: lossLimit
        });
        setLocalSettings(prev => ({ ...prev, [shownDateKey]: today, [shownLimitKey]: lossLimit }));
      } else if (netLossAbs < lossLimit && currentSettings[shownDateKey] === today) {
        await updateSettingsFunc(userId, accountId, {
          [shownDateKey]: "",
          [shownLimitKey]: 0
        });
        setLocalSettings(prev => ({ ...prev, [shownDateKey]: "", [shownLimitKey]: 0 }));
      }
    } catch (e) {
      console.error("checkDailyLossLimit error:", e);
    }
  }


const handleCloseSaveConfirmation = async () => {
  FXDBG("handleCloseSaveConfirmation() - closing saved-confirmation dialog");
  setShowTradeSaved(false);

  if (postSaveCheckInfo) {
    FXDBG("handleCloseSaveConfirmation() - post-save minimal checks (account goal only)");
    popupsAllowedRef.current = true;    // allow queuing while we run the single check
    try {
      await checkAccountGoal(
        postSaveCheckInfo.settings ?? settings,
        updateSettings,
        userId,
        accountId,
        setSettings
      );
    } finally {
      popupsAllowedRef.current = false; // close the gate
      setPostSaveCheckInfo(null);
    }
  } else {
    FXDBG("handleCloseSaveConfirmation() - no postSaveCheckInfo present");
  }
};



  // === Checks ===
  async function checkConsecutiveWins(
    trades,
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const today = getTodayLocalISO();

    // Only look at today's trades
    const todayTrades = (trades || []).filter(t => (t["Date"] || "").startsWith(today));
    if (todayTrades.length < 5) return;

    // Be tolerant if "Win" can be string/boolean
    const isWin = v => v === true || String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

    // Check if the last 5 trades are wins
    const last5 = todayTrades.slice(-5);
    const popupId = "consecutiveWinsPopup";
    const shownDateKey = "ConsecutiveWinsPopupShownDate";

    // Only queue the popup if the last 5 trades are wins AND it hasn't been shown today
    if (last5.every(t => isWin(t["Win"])) && currentSettings[shownDateKey] !== today) {
      queuePopup(popupId);
      const newSettings = { [shownDateKey]: today };
      await updateSettingsFunc(userId, accountId, newSettings);
      setLocalSettings(prev => ({ ...prev, ...newSettings }));
    }
    // The "else if" block that previously reset the flag for the same day has been removed.
  }


  function isTrueLoss(val, pnl) {
    if (typeof val === "boolean") return val;
    if (typeof val === "string") return val.toLowerCase() === "true";
    // final fallback: if we have P&L, infer a loss from it
    if (pnl !== undefined && pnl !== null && !isNaN(Number(pnl))) {
      return Number(pnl) < 0;
    }
    return false;
  }

  async function checkOvertrading(trades, maxTradesPerDay, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
     const today = getTodayLocalISO();
     const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
     const popupId = "overtradingPopup";
     const shownDateKey = "OvertradingPopupShownDate";

     if (todayTrades.length > maxTradesPerDay && currentSettings[shownDateKey] !== today) {
       queuePopup(popupId, { maxTradesPerDay });
       // FIX: Define 'settingsUpdate' as a partial object
       const settingsUpdate = { [shownDateKey]: today };
       // FIX: Use the correct variable 'settingsUpdate'
       await updateSettingsFunc(userId, accountId, settingsUpdate);
       setLocalSettings(prev => ({ ...prev, ...settingsUpdate }));
     } else if (todayTrades.length <= maxTradesPerDay && currentSettings[shownDateKey] === today) {
       // FIX: Define 'settingsUpdate' as a partial object
       const settingsUpdate = { [shownDateKey]: "" };
       // FIX: Use the correct variable 'settingsUpdate'
       await updateSettingsFunc(userId, accountId, settingsUpdate);
       setLocalSettings(prev => ({ ...prev, ...settingsUpdate }));
     }
   }
  async function checkTooManyLosses(
    trades,
    maxLossesPerDay,
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const today = getTodayLocalISO();

    // Be tolerant of boolean/strings in the "Loss" field
    const isLoss = (v) => String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

    const todayLosses = trades.filter(
      (t) => (t["Date"] || "").startsWith(today) && isLoss(t["Loss"])
    );

    const popupId = "tooManyLossesPopup";
    const shownDateKey = "TooManyLossesPopupShownDate";

    // Fire when the limit is REACHED (>=), not only when it's exceeded
    if (todayLosses.length >= maxLossesPerDay && currentSettings[shownDateKey] !== today) {
      queuePopup(popupId, { maxLossesPerDay });
      const newSettings = { ...currentSettings, [shownDateKey]: today };
      await updateSettingsFunc(userId, accountId, newSettings);
      setLocalSettings(newSettings);
    }
    // Reset once you're back UNDER the limit
    else if (todayLosses.length < maxLossesPerDay && currentSettings[shownDateKey] === today) {
      const newSettings = { ...currentSettings, [shownDateKey]: "" };
      await updateSettingsFunc(userId, accountId, newSettings);
      setLocalSettings(newSettings);
    }
  }

  // helper: returns the first finite, positive number or 0
  function firstPositive(...vals) {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  }


 async function checkMaxLossLimitBlown(
   currentSettings,
   updateSettingsFunc,
   userId,
   accountId,
   setLocalSettings
 ) {
   const today = getTodayLocalISO();
   const shownKey = "MaxLossLimitBlownShownDate";

   // Retail keys (no "Prop" references for the regular forex screen)
   const base = Number(currentSettings["Initial Balance"] || 0);
   const rb   = Number(currentSettings["Running Balance"] || 0);

   // If an admin ever sets a non-zero limit, respect it; otherwise retail fallback.
   const uiMax     = Number(currentSettings["Max Loss Limit"] || 0);
   const legacyMax = Number(currentSettings["Maximum Loss Limit"] || 0);
   const maxLoss   = Math.max(uiMax, legacyMax); // use whichever is set

   // Retail fallback:
   // - If maxLoss > 0, use (base - maxLoss), i.e. prop-like rule
   // - If maxLoss == 0 (retail), treat "blown" as balance <= 0, no UI field needed
   const isRetailMode = !(Number.isFinite(maxLoss) && maxLoss > 0 && Number.isFinite(base) && base > 0);
   const threshold = isRetailMode ? 0 : (base - maxLoss);

   const blownNow = Number.isFinite(rb) && rb <= threshold;

   if (blownNow && currentSettings[shownKey] !== today) {
     queuePopup("maxLossLimitBlown", {
       mode: isRetailMode ? "retail" : "max-loss",
       base,
       maxLoss,
       threshold,
       rb
     });
     await updateSettingsFunc(userId, accountId, { [shownKey]: today });
     setLocalSettings(prev => ({ ...prev, [shownKey]: today }));
   } else if (!blownNow && currentSettings[shownKey] === today) {
     // Clear guard if user is back above the threshold so it can re-trigger later
     await updateSettingsFunc(userId, accountId, { [shownKey]: "" });
     setLocalSettings(prev => ({ ...prev, [shownKey]: "" }));
   }
 }



 
async function checkMoodVolatility(
  trades, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings
) {
  const today = todayLocalISO();
  const shownDateKey = "MoodVolatilityPopupShownDate";

  // bail if already shown today
  if (wasShownToday(currentSettings, shownDateKey)) return;

  if (!Array.isArray(trades)) return;
  const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
  if (todayTrades.length < 3) return;

  const last3 = todayTrades.slice(-3);
  const moods = last3.map(t => {
    const v = Number(t["Mood_Mindset"] ?? t["Mood"] ?? t["Mood / Mindset"]);
    return Number.isFinite(v) ? v : null;
  });

  const allHigh = moods.length === 3 && moods.every(v => v !== null && (v === 4 || v === 5));
  if (allHigh) {
    queuePopup("moodVolatilityPopup");
    await markShownToday(shownDateKey, updateSettingsFunc, userId, accountId, setLocalSettings);
  }
}



async function checkDrawdown(
  trades,
  drawdownLimitPct,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  try {
    if (!Array.isArray(trades) || !(Number(drawdownLimitPct) > 0)) return;

    const todayISO = getTodayLocalISO();
    const POPUP_ID  = "drawdownWarning";
    const SHOWN_KEY = "DrawdownPopupShownDate"; // legacy: we keep this for analytics/compat

    // helpers
    const toNum = (v) => {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
      try { return Number(String(v ?? "").replace(/[^\d.-]/g, "")); } catch { return NaN; }
    };
    const getBal = (row) => {
      const rb = toNum(row?.["Running Balance"]);
      if (rb > 0) return rb;
      const ab = toNum(row?.["Actual Balance"]);
      return ab > 0 ? ab : NaN;
    };
    const rowTs = (r) => {
      const d = r?.["Date"];
      let base = d?.seconds ? new Date(d.seconds * 1000)
               : d ? new Date(d) : new Date(0);
      const t = r?.["Exit Time"] || r?.["Entry Time"] || "";
      let secs = 0;
      try {
        const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([APap][Mm])?$/);
        if (m) {
          let hh = +m[1], mm = +m[2], ss = +(m[3] || 0);
          const ap = m[4];
          if (ap) { if (/pm/i.test(ap) && hh !== 12) hh += 12; if (/am/i.test(ap) && hh === 12) hh = 0; }
          secs = hh * 3600 + mm * 60 + ss;
        }
      } catch {}
      return base.getTime() + secs * 1000;
    };

    // sort + equity series
    const sorted = [...trades].sort((a, b) => {
      const na = +((a?.tradeNum ?? a?.["Trade #"]) || NaN);
      const nb = +((b?.tradeNum ?? b?.["Trade #"]) || NaN);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return rowTs(a) - rowTs(b);
    });

    const series = sorted.map(getBal).filter((n) => Number.isFinite(n) && n > 0);
    const initial = toNum(currentSettings?.["Initial Balance"]);
    if (initial > 0) series.push(initial);
    if (!series.length) return;

    let last = NaN;
    for (let i = sorted.length - 1; i >= 0; i--) {
      const b = getBal(sorted[i]);
      if (Number.isFinite(b) && b > 0) { last = b; break; }
    }
    if (!(last > 0)) return;

    const peak = Math.max(...series);
    const ddAmount = +(peak - last).toFixed(2);
    const ddPct = ((peak - last) / peak) * 100;

    // lock state
    const lockActive  = !!currentSettings?.DrawdownLockActive;
    const lockPeak    = +currentSettings?.DrawdownLockPeak || 0;
    const shownLegacy = String(currentSettings?.[SHOWN_KEY] || "");

    const overLimit = ddPct >= Number(drawdownLimitPct);

    // Migrate legacy “shown today” → lock once
    if (overLimit && !lockActive && shownLegacy) {
      const patch = {
        DrawdownLockActive: true,
        DrawdownLockPeak: peak,
        DrawdownLockStartedDate: shownLegacy,
      };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings?.(p => ({ ...p, ...patch }));
      return; // don’t re-show immediately
    }

    if (overLimit) {
      // EDGE TRIGGER: cross into drawdown from below
      if (!lockActive) {
        queuePopup(POPUP_ID, { drawdownLimitPct: Number(drawdownLimitPct), drawdownAmount: ddAmount, drawdownPct: ddPct });
        const patch = {
          [SHOWN_KEY]: todayISO,            // keep for analytics
          DrawdownLockActive: true,
          DrawdownLockPeak: peak,
          DrawdownLockStartedDate: todayISO,
        };
        await updateSettingsFunc(userId, accountId, patch);
        setLocalSettings?.(p => ({ ...p, ...patch }));
      }
      return; // stay silent while locked
    }

    // Re-armed only after recovery
    if (lockActive || shownLegacy) {
      const patch = {
        [SHOWN_KEY]: "",
        DrawdownLockActive: false,
        DrawdownLockPeak: 0,
        DrawdownLockStartedDate: "",
      };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings?.(p => ({ ...p, ...patch }));
    }
  } catch (e) {
    console.error("checkDrawdown error:", e);
  }
}




  // NEW — compute today's net profit and realized losses from journal rows
  // Compute today's net P/L and loss-beyond-wins from journal rows
  function computeDailyProgressFromRows(rows = []) {
    const today = getTodayLocalISO();
    const todays = rows.filter(t => (t["Date"] || "").startsWith(today));

    const net = todays.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);

    // Green bar (goal): only net profit if positive
    setDailyProfit(Math.max(0, net));

    // Red bar (loss limit): only losses beyond wins (0 while net >= 0)
    setDailyLossAbs(Math.max(0, -net));
  }

// Recomputes today's progress bars.
// If `rowsOpt` is passed, uses it (so we can include the just-saved trade);
// otherwise fetches from Firestore.

 async function updateDailyProgress(rowsMaybe) {
   try {
     const rows = Array.isArray(rowsMaybe)
       ? rowsMaybe
       : await fetchJournalRows(userId, accountId);

     const todayISO = getTodayLocalISO();
     const todays = (rows || []).filter(r => (r?.["Date"] || "").startsWith(todayISO));

     // Net P&L for today (wins - losses)
     const net = todays.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);

     const goal      = Number(settings["Daily Goal"] || 0);
     const lossLimit = Number(settings["Daily Loss Limit"] || 0);

     // Amounts we display
     const netPositive      = Math.max(0, net);   // for Daily Goal (green)
     const lossesBeyondWins = Math.max(0, -net);  // for Daily Loss Limit (red)

     // Percentages (UNCAPPED so the bar can overflow)
     const pctGoal = goal > 0 ? (netPositive / goal) * 100 : 0;
     const pctLoss = lossLimit > 0 ? (lossesBeyondWins / lossLimit) * 100 : 0;

     setDailyGoalProg({ done: netPositive, target: goal, pct: pctGoal });
     setDailyLossProg({ lost: lossesBeyondWins, limit: lossLimit, pct: pctLoss });
     if (typeof setDailyLossBeyondWins === "function") {
       setDailyLossBeyondWins(lossesBeyondWins);
     }
   } catch (e) {
     console.error("Failed to update daily progress", e);
   }
 }





// Prefer the Account Goal popup; only show Daily Goal if account goal not hit yet
// Keep this near your other checks
async function checkDailyProfitTarget(
  trades,
  dailyGoal,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();
  const popupId = "dailyGoalPopup";
  const shownDateKey = "DailyGoalPopupShownDate";

  // ✅ Once-per-day guard (ignore goal changes)
  if (currentSettings?.[shownDateKey] === today) return;

  const todayTrades = Array.isArray(trades)
    ? trades.filter(t => (t?.["Date"] || "").startsWith(today))
    : [];

  const totalProfit = todayTrades.reduce(
    (sum, t) => sum + (Number(t?.["Actual Reward"]) || 0),
    0
  );

  if (dailyGoal > 0 && totalProfit >= dailyGoal) {
    queuePopup(popupId, {
      message: "Congratulations on hitting your daily goal!",
      dailyGoal
    });

    // ✅ Mark as shown for today only
    await updateSettingsFunc(userId, accountId, { [shownDateKey]: today });
    setLocalSettings(prev => ({ ...prev, [shownDateKey]: today }));
  }
}



async function checkWin10NoRulesPopup(
  trade,
  settings,
  updateSettings,
  userId,
  accountId,
  setLocalSettings,
  queuePopup
) {
  const today = getTodayLocalISO();
  const key = "Win10NoRulesPopupShownDate";

  // --- helpers ---
  const isWin = (v) => {
    const s = String(v).trim().toLowerCase();
    return v === true || s === "true" || s === "yes" || s === "win" || s === "won" || s === "1";
  };

  const saidNo = (v) => String(v ?? "").trim().toLowerCase() === "no";

  const parsePercent = (raw) => {
    if (raw == null) return NaN;
    const str = String(raw).trim();
    // Remove commas and spaces around percent sign
    const hasPercent = str.includes("%");
    const cleaned = str.replace(/%/g, "").replace(/,/g, "").trim();
    const n = Number(cleaned);
    if (Number.isNaN(n)) return NaN;

    // If already marked with %, treat as percent units as-is
    if (hasPercent) return n;

    // If value looks like a fraction (0 < n <= 1), interpret as 0.12 => 12%
    if (n > 0 && n <= 1) return n * 100;

    // Otherwise assume it's already in percent units (e.g., 10 or 12.5)
    return n;
  };

  const pct = parsePercent(trade["Actual Percent Growth"]);

  // If we've already shown it today, don't show again or clear it this same day
  if (settings[key] === today) return;

  // Check both "Did I follow my Rules?" spellings robustly
  const rulesField = trade["Did I follow my Rules?"] ?? trade["Did I follow my Rules"];

  if (isWin(trade["Win"]) && saidNo(rulesField) && pct >= 10) {
    queuePopup("win10NoRulesPopup", {
      message:
        "Winning BIG is always exciting!\n\n However you want to be careful. Having a big win without following your rules can be dangerous for your mindset.\n\nContinuing to do this could rewire your brain to seek dopamine hits instead of the true accomplishment of following your process.\n\nThis is another way to easily lose control and go on tilt.\n\nTreat this as a neutral event, and be mindful not to get hooked by big wins that happen outside your plan.\n\nDiscipline > excitement!",
    });
    const newSettings = { ...settings, [key]: today };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }

  // Note: we intentionally do NOT clear the key on non-qualifying trades the same day.
  // This prevents the popup from being "un-shown" after it has triggered once.
}


  async function checkAccountGoal(
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const today = getTodayLocalISO();
    const shownKey = "AccountGoalReachedShownDate";
    const goal = Number(currentSettings["Account Goal"] || 0);
    const rb   = Number(currentSettings["Running Balance"] || 0);

    if (goal > 0 && rb >= goal && currentSettings[shownKey] !== today) {
      queuePopup("accountGoalReached", { balance: rb, goal });
      // ✅ Partial update (don’t overwrite other settings)
      await updateSettingsFunc(userId, accountId, { [shownKey]: today });
      setLocalSettings(prev => ({ ...prev, [shownKey]: today }));
    } else if (rb < goal && currentSettings[shownKey] === today) {
      // ✅ Clear if we dip back under
      await updateSettingsFunc(userId, accountId, { [shownKey]: "" });
      setLocalSettings(prev => ({ ...prev, [shownKey]: "" }));
    }
  }


  async function checkWin10WithRulesPopup(trade, settings, updateSettings, userId, accountId, setLocalSettings, queuePopup) {
    const today = new Date().toISOString().slice(0, 10);
    const key = "Win10WithRulesPopupShownDate";
    if (
      trade["Win"] === true &&
      Number(trade["Actual Percent Growth"]?.replace("%", "")) >= 20 &&
      (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) === "Yes" &&
      settings[key] !== today
    ) {
      queuePopup("win10WithRulesPopup", {
        message: "Congratulations! You followed your rules and had a BIG win! \n\nDon't get over confident, stay consistent and continue following your rules. \n\nContinue being Awesome!",
        confetti: false
      });
      const newSettings = { ...settings, [key]: today };
      await updateSettings(userId, accountId, newSettings);
      setLocalSettings(newSettings);
    } else if (
      (trade["Win"] !== true ||
        Number(trade["Actual Percent Growth"]?.replace("%", "")) < 20 ||
        (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) !== "Yes") &&
      settings[key] === today
    ) {
      const newSettings = { ...settings, [key]: "" };
      await updateSettings(userId, accountId, newSettings);
      setLocalSettings(newSettings);
    }
  }

  async function checkLessonsLearnedStreak(
    trades,
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const todayISO = getTodayLocalISO();

    // Count how many of today's rows have a lesson logged (text present OR LogLesson true)
    const lessonsToday = (trades || []).filter(t => {
      const d = t["Date"];
      let isToday = false;
      if (!d) return false;
      if (typeof d === "string") {
        isToday = d.slice(0, 10) === todayISO || d.startsWith(todayISO);
      } else if (d?.seconds) {
        isToday = new Date(d.seconds * 1000).toISOString().slice(0, 10) === todayISO;
      } else {
        try { isToday = new Date(d).toISOString().slice(0, 10) === todayISO; } catch { isToday = false; }
      }
      if (!isToday) return false;

       const loggedFlag = t["LogLesson"] === true || String(t["LogLesson"]).toLowerCase() === "true";
       return loggedFlag; // only count lessons that were explicitly logged
    }).length;

    if (lessonsToday < 3) {
      // Optional: if previously shown today but the count fell below 3 (e.g., user edited a trade), clear the flag
      if (currentSettings["LessonsLearnedStreakWarningShownDate"] === todayISO) {
        const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: "" };
        await updateSettingsFunc(userId, accountId, next);
        setLocalSettings(next);
      }
      return;
    }

    // Fire once per day when the threshold (>= 3) is reached
    if (currentSettings["LessonsLearnedStreakWarningShownDate"] !== todayISO) {
      queuePopup("lessonsLearnedStreakWarning", { lessonsToday });
      const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: todayISO };
      await updateSettingsFunc(userId, accountId, next);
      setLocalSettings(next);
    }
  }


    async function checkRiskOfRuin(
      lossProjection,
      currentSettings,
      updateSettingsFunc,
      userId,
      accountId,
      setLocalSettings
    ) {
      const today = getTodayLocalISO();
      const popupId = "riskOfRuinWarning";
      const shownDateKey = "RiskOfRuinWarningShownDate";

      // Use retail balances for the retail forex form
      const initial = Number(currentSettings["Initial Balance"] || 0);
      const balance = Number(currentSettings["Running Balance"] || 0);

      if (!Number.isFinite(initial) || initial <= 0) return; // can't evaluate
      const threshold = initial * 0.25;

      if (balance <= threshold && currentSettings[shownDateKey] !== today) {
        // Pass values for optional display; include tradeCount so current UI can still reference it
        queuePopup(popupId, {
          initial,
          balance,
          threshold,
          tradeCount: lossProjection?.tradeCount
        });

        const newSettings = { ...currentSettings, [shownDateKey]: today };
        await updateSettingsFunc(userId, accountId, newSettings);
        setLocalSettings(newSettings);
      } else if (balance > threshold && currentSettings[shownDateKey] === today) {
        // If you've recovered above the threshold, clear today's guard
        const newSettings = { ...currentSettings, [shownDateKey]: "" };
        await updateSettingsFunc(userId, accountId, newSettings);
        setLocalSettings(newSettings);
      }
    }

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function formatThreeLossMessage(line) {
  if (!line) return null;

  const highlights = {
    "Take a break!": "font-bold text-red-500 dark:text-red-500",
    "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
  };

  // Escape phrases so regex treats them literally
  const escaped = Object.keys(highlights).map(escapeRegExp).join("|");
  const regex = new RegExp(`(${escaped})`, "g");

  return line.split(regex).map((part, i) => {
    return highlights[part]
      ? <span key={i} className={highlights[part]}>{part}</span>
      // ⬇️ route anything else through the tilt tooltip wrapper
      : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>;
  });
}

  // Shows a warning *only* after the daily goal has been reached AND the next two trades are losses.
  async function checkTwoLossAfterGoal(trades, settings, updateSettingsFunc, userId, accountId, setLocalSettings, queuePopupFunc) {
    const today = getTodayLocalISO();
     const shownKey = "TwoLossAfterGoalWarningShownDate";
     const dailyGoal = Number(settings["Daily Goal"] || 0);

    const todayTrades = (trades || []).filter(t => (t["Date"] || "").startsWith(today));

     let runningProfit = 0;
     let goalReachedAtIndex = -1;

     for (let i = 0; i < todayTrades.length; i++) {
       runningProfit += Number(todayTrades[i]["Actual Reward"] || 0);
       if (runningProfit >= dailyGoal && goalReachedAtIndex === -1) {
         goalReachedAtIndex = i;
       }
     }
     if (goalReachedAtIndex === -1) return;

    const tradesAfterGoal = todayTrades.slice(goalReachedAtIndex + 1);
     // Trigger on any later pair of consecutive losses after the goal
     let twoLosses = false;
     let streak = 0;
     for (const t of tradesAfterGoal) {
       if (isTrueLoss(t["Loss"], t["Actual Reward"])) {
         streak += 1;
         if (streak >= 2) { twoLosses = true; break; }
       } else {
         streak = 0;
       }
     }

    if (twoLosses && settings[shownKey] !== today) {
       queuePopupFunc("twoLossAfterGoalWarning", {
         message:
           "You have taken 2 losses after reaching your daily goal.\n\nYou could easily go on tilt if you continue to lose trying to get it back. \n\nConsider stopping for the day to protect your gains."
       });

      const patch = { [shownKey]: today };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings(prev => ({ ...prev, ...patch }));
    }
     }





// Function to add a popup to the queue (guarded) with priority handling
     const queuePopup = (id, data = {}) => {
       const today = getTodayLocalISO();
       FXDBG("queuePopup called", { id, allowed: popupsAllowedRef.current, today, alreadyToday: shownTodayRef.current[id] });

       // Only queue when allowed (we flip this in handleSaveTrade / handleCloseSaveConfirmation)
       if (!popupsAllowedRef.current) {
         FXDBG("queuePopup BLOCKED: popupsAllowedRef.current=false", { id });
         return;
       }

       // In-session per-day guard (for daily-scoped popups)
       if (DAILY_SCOPED_IDS.has(id) && shownTodayRef.current[id] === today) {
         FXDBG("queuePopup SKIPPED: already shown today", { id, today });
         return;
       }

       setPopupQueue(prev => {
         let next = prev;

         // Priority handling (unchanged), but log when we suppress
         if (id === "twoLossAfterGoalWarning") {
           if (next.some(p => p.id === "twoLossWarning")) {
             FXDBG("queuePopup: removing lower-priority 'twoLossWarning'");
           }
           next = next.filter(p => p.id !== "twoLossWarning");
         }
         if (id === "twoLossWarning") {
           const higherPending = next.some(p => p.id === "twoLossAfterGoalWarning");
           const higherShowing = currentPopup?.id === "twoLossAfterGoalWarning";
           if (higherPending || higherShowing) {
             FXDBG("queuePopup SKIPPED: higher priority 'twoLossAfterGoalWarning' pending/showing");
             return next;
           }
         }

         // Avoid duplicates or re-adding the currently displayed popup
         if (next.some(p => p.id === id) || currentPopup?.id === id) {
           FXDBG("queuePopup SKIPPED: duplicate or currently showing", { id });
           return next;
         }

         // Mark as seen for today if this is daily-scoped
         if (DAILY_SCOPED_IDS.has(id)) shownTodayRef.current[id] = today;

         const updated = [...next, { id, data }];
         FXDBG("queuePopup ENQUEUED", { id, data, queueLen: updated.length });
         return updated;
       });
     };




  const closeCurrentPopup = () => setCurrentPopup(null);

  // Manage popup queue
  useEffect(() => {
    if (currentPopup === null && popupQueue.length > 0) {
      const next = popupQueue[0];
      setPopupQueue(prev => prev.slice(1));
      setCurrentPopup(next);
    }
  }, [popupQueue, currentPopup]);

useEffect(() => {
    if (!userId) return;
    getUserSettings(userId).then(data => {
      // If the prefs doc doesn't exist (data = {}), set default prefs!
      if (!data || Object.keys(data).length === 0) {
        // Pick your defaults here
        const defaultPrefs = {
          "Pretrade Checklist": "Yes",   // Or "No" if you want off by default
          "PretradeChecklistShownDate": ""
          // ...add any other default user prefs you want here
        };
        setUserPrefs(defaultPrefs);
        // ✅ Cache default to local storage
        localStorage.setItem("isPrivacyMode", "false"); 
        updateUserSettings(userId, defaultPrefs); // Save to Firestore
      } else {
        setUserPrefs(data);
        // ✅ Cache fetched setting to local storage
        localStorage.setItem("isPrivacyMode", data["Privacy Mode"] === true ? "true" : "false"); 
      }
    });
  }, [userId]);


      // 2) Decide whether to show checklist (local date aware)
  useEffect(() => {
    const today = getTodayLocalISO();
    const wants = userPrefs["Pretrade Checklist"] === "Yes";
    const last  = userPrefs["PretradeChecklistShownDate"] || "";
    setShowChecklist(wants && last !== today);
  }, [userPrefs]);

  useEffect(() => {
    if (!userId || !accountId) return;
    updateDailyProgress();
  }, [userId, accountId, settings["Daily Goal"], settings["Daily Loss Limit"]]);


function normalizeMaxLossKeys(obj = {}) {
  const ui = Number(obj["Max Loss Limit"]);
  const legacy = Number(obj["Maximum Loss Limit"]);
  const max = Number.isFinite(ui) && ui > 0 ? ui : (Number.isFinite(legacy) ? legacy : 0);
  if (!(max > 0)) return obj;
  return { ...obj, "Max Loss Limit": max, "Maximum Loss Limit": max };
}

  async function refreshAfterSave(userId, accountId) {
    const fresh = await getSettings(userId, accountId);
    setSettings(prev => ({ ...prev, ...fresh }));



    const {
      Symbol,
      "Running Balance": rb,
      "Risk Percentage": rp,
      "Stop Loss": sl,
      "Take Profit": tp,
      "Max Lot Size": maxL,
      "Min Lot Size": minL,
      "Account Goal": goal,
      "Pip Value": pvRaw,
      "Account Currency": accountCurrency
    } = fresh;

    const pv = Number(pvRaw) || getPipValue(Symbol, 1, accountCurrency);
    // Keep the display budget in $ for both modes
    const percentOfBalance = riskBudgetDollar(fresh, Number(rb));
    const stopAmt = getEffectiveDistance(fresh, "Stop Loss");
    const tpAmt = getEffectiveDistance(fresh, "Take Profit");
    let riskRewardRatio = "";
    if (tpAmt && stopAmt) {
      const rr = tpAmt / stopAmt;
      riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
    }

     // Size from the active risk mode (convert $ -> % when needed)
    const lots = getSuggestedLotSize(
      Number(rb),
      riskPercentFromDollar(fresh, Number(rb)),
      Number(stopAmt),
      Number(pv),
      Number(minL),
      Number(maxL)
    );
    const totalRisk = (lots > 0 ? lots : Math.max(Number(minL), 0.01)) * Number(sl) * Number(pv);
    const targetReward = (lots > 0 ? lots : Math.max(Number(minL), 0.01)) * Number(pv) * Number(tp);
    const { perLot, label } = getUnitsMeta(Symbol);
    const units = lots > 0 ? lots * perLot : 0;
    const riskMode   = getRiskMode(settings);                  // "%" or "$"
const riskDollar = Number(settings["Risk Dollar"] || 0);   // constant $/trade

    setCalculatedSummary({
      riskRewardRatio,
      percentOfBalance,                // $ budget for the Summary
      riskPercent: riskPercentFromDollar(fresh, Number(rb)), // show % equivalent
      stopLoss: sl,
      takeProfit: tp,
      stopAmt, // NEW
      tpAmt,   // NEW
      lots,
      minLot: minL,
      totalRisk,
      targetReward,
      units,
      unitsLabel: label,
      riskMode, riskDollar,
      pipValue: pv
    });

    setWinProjection(
      getCompoundingProjection({
        balance: Number(rb),
        goal: Number(goal),
        riskPercent: Number(rp),
        stopLoss: Number(sl),
        takeProfit: Number(tp),
        maxLot: Number(maxL),
        minLot: Number(minL),
        pipValue: Number(pv)
      })
    );

    setLossProjection(
      getLossProjection({
        balance: Number(rb),
        stopLoss: Number(sl),
        pipValue: Number(pv),
        minLot: Number(minL),
        maxLot: Number(maxL),
        riskPercent: Number(rp),
        riskMode, riskDollar
      })
    );

    const initial = Number(fresh["Initial Balance"] || 0);
    const goalNum = Number(fresh["Account Goal"] || 0);
    const running = Number(fresh["Running Balance"] || 0);
    let pct = 0;
    if (goalNum > initial) pct = ((running - initial) / (goalNum - initial)) * 100;
    setProgress(Math.max(0, Math.min(100, pct)));
  }



  // --- Handlers ---
  const numericFields = [
    "Initial Balance",
    "Max Lot Size",
    "Stop Loss",
    "Daily Goal",
    "Min Lot Size",
    "Take Profit",
    "Daily Loss Limit",
    "Account Goal",
    "Running Balance",
    "Risk Percentage",
    "Leverage",
    "Pip Value",
    "Max Loss Limit",
    "IdealSL"
  ];

const handleSettingChange = (key, value) => {
    popupsAllowedRef.current = false; // ensure queue is OFF while editing

    // Keep legacy + new names in sync
    if (key === "Max Loss Limit" || key === "Maximum Loss Limit") {
      const n = Number(value) || 0;
      setSettings(prev => ({ ...prev, "Max Loss Limit": n, "Maximum Loss Limit": n }));
      setCalculatedSummary(null);
      setHasCalculated(false);
      setShowRiskWarningPopup(false);
      return;
    }

    // Clearable risk inputs: keep raw string until blur/commit
    setSettings(prev => {
      const isNumeric = numericFields.includes(key);
      const next = { ...prev };

      // --- CLEAR VALUES ON MODE SWITCH ---
      if (key === "Stop Loss Mode") next["Stop Loss"] = "";
      if (key === "Take Profit Mode") next["Take Profit"] = "";

      if (RISK_KEYS.has(key)) {
        next[key] = value; // "" allowed while typing
      } else if (isNumeric) {
        next[key] = value === "" ? "" : Number(value);
      } else {
        next[key] = value;
      }

      if (key === "Initial Balance") next["Running Balance"] = Number(value);
      
      // Auto-pip value on symbol change
      if (key === "Symbol" || key === "Product") {
        const pv = getPipValue(value, 1, prev["Account Currency"] || next["Account Currency"]);
        next["Pip Value"] = pv;
      }
      return next;
    });

    // FIX: Prepare override using 'value' so the background engine knows you cleared it
    const summaryOverride = { [key]: value };
  if (key === "Stop Loss Mode") summaryOverride["Stop Loss"] = "";
  if (key === "Take Profit Mode") summaryOverride["Take Profit"] = "";

  setCalculatedSummary(null);
  setHasCalculated(false);
  setShowRiskWarningPopup(false);

  // Instantly calculate with the overridden values
  runSummaryOnly(summaryOverride);

  // Restore the background auto-save functionality:
  const newSettingsToSave = { ...settings, ...summaryOverride };
  updateSettings(userId, accountId, newSettingsToSave).catch(err => 
    console.error("Auto-save failed:", err)
  );
};

  const handleJournalChange = (key, value) => setJournal(prev => ({ ...prev, [key]: value }));

  // Check daily goal popup on load
  useEffect(() => {
    if (!settings || !userId || !accountId) return;
    async function checkDailyGoalPopupOnLoad() {
      try {
        const freshSettings = await getSettings(userId, accountId);
        await checkDailyProfitTarget([], Number(freshSettings["Daily Goal"] || 0), freshSettings, updateSettings, userId, accountId, setSettings);
      } catch (err) {
        console.error("Failed to check daily goal popup on load:", err);
      }
    }
    checkDailyGoalPopupOnLoad();
  }, [settings, userId, accountId]);

  // Summary-only auto run (no popups) if all fields present
  const AUTO_CALC_FIELDS = [
    "Symbol",
    "Initial Balance",
    "Max Lot Size",
    "Stop Loss",
    "Daily Goal",
    "Min Lot Size",
    "Take Profit",
    "Daily Loss Limit",
    "Account Goal",
    "Running Balance",
    "Risk Percentage",
    "Pip Value"
  ];
// Summary-only auto run (no popups) — run ONCE right after initial load only
useEffect(() => {
  if (!loadedFromServer.current) return;           // only after Firestore load
  if (hasRunInitialAutoCalc.current) return;       // only run once per load

  const allFieldsPresent = AUTO_CALC_FIELDS.every(key =>
    typeof settings[key] !== "undefined" &&
    settings[key] !== "" &&
    (key === "Symbol" ? !!settings[key] : Number(settings[key]) > 0)
  );

  if (allFieldsPresent && !calculatedSummary) {
    runSummaryOnly();
  }

  // mark done even if we didn't calculate (prevents running later while user types)
  hasRunInitialAutoCalc.current = true;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [settings]);

function runSummaryOnly(override = {}) {
  // merge in any fresh values (like Running Balance right after save)
  const s = { ...settings, ...override };

  const {
    Symbol,
    "Running Balance": rb,
    "Risk Percentage": rp,
    "Max Lot Size": maxL,
    "Min Lot Size": minL,
    "Account Goal": goal,
    "Pip Value": pvRaw
  } = s;

  const pv = Number(pvRaw) || getPipValue(Symbol, 1, s["Account Currency"]);
  const entryPrice = Number(s["Entry Price"] || 0);

  // --- NEW: DYNAMIC EXIT LOGIC ---
const stopAmt = getEffectiveDistance(s, "Stop Loss");
      const tpAmt = getEffectiveDistance(s, "Take Profit");
      const sl = stopAmt; 
      const tp = tpAmt;
  // -------------------------------

  // --- NEW: compute budget from % or $ mode
  const baseBal = Number(rb) || 0;
  const riskBudget = riskBudgetDollar(s, baseBal);            // currency
  const riskPercentDisplay = riskPercentFromDollar(s, baseBal); // percent

  let riskRewardRatio = "";
  if (tp && sl) {
    const rr = tp / sl;
    riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
  }

  // Normalize risk mode and resolve any legacy dollar keys
  const riskMode = (() => {
    const v = String(s["Risk Mode"] ?? "%").trim().toLowerCase();
    return (v === "$" || v.startsWith("d")) ? "$" : "%";
  })();
  const riskDollar = Number(
    s["Risk Dollar"] ?? s["Risk Amount"] ?? s["Risk Dollar Amount"] ?? 0
  );


  // Size using the active risk mode (converts $ -> % when needed)
  const lots = getSuggestedLotSize(
    rb,
    riskPercentFromDollar(typeof s !== "undefined" ? s : settings, rb),
    sl, pv, minL, maxL
  );
  const totalRisk = (lots > 0 ? lots : minL) * sl * pv;
  const targetReward = (lots > 0 ? lots : minL) * pv * tp;
  const { perLot, label } = getUnitsMeta(Symbol);
  const units = lots > 0 ? lots * perLot : 0;


  setCalculatedSummary({
    riskRewardRatio,
    percentOfBalance: riskBudget,     // always dollar budget (works for % & $ modes)
    riskPercent: riskPercentDisplay,  // always % for display
    stopLoss: sl,
    takeProfit: tp,
    stopAmt: sl,    
    tpAmt: tp,
    lots,
    minLot: minL,
    totalRisk,
    targetReward,
    units,
    unitsLabel: label,
    riskMode, riskDollar,
    pipValue: pv
  });


  setJournal(prev => ({ ...prev, Size: lots }));

   setWinProjection(
     getCompoundingProjection({
       balance: rb,
       goal,
       riskMode,               // ← add
       riskPercent: rp,
       riskDollar,             // ← add
       stopLoss: sl,
       takeProfit: tp,
       maxLot: maxL,
       minLot: minL,
       pipValue: pv
     })
   );

    setLossProjection(
      getLossProjection({
        balance: rb,
        stopLoss: sl,
        pipValue: pv,
        minLot: minL,
        maxLot: maxL,
        riskMode,               // ← add
        riskPercent: rp,
        riskDollar              // ← add
      })
    );

  setShowRiskWarningPopup(false);
}




 // Run after the user clicks "OK" on the Trade Saved popup.
 async function runAllPopupChecks(allRows, currentSettings, lossProjection, startBal, pnl) {
   try {
     const today = getTodayLocalISO();
     let settingsForChecks = { ...currentSettings };

 // Normalize balances from the just-saved trade so all checks use a consistent RB
if (Number.isFinite(startBal) && Number.isFinite(pnl)) {
  const computedRB = Number(startBal) + Number(pnl);
  if (computedRB > 0) {
    settingsForChecks["Running Balance"] = computedRB;  // retail only
    // settingsForChecks["Prop Running Balance"] = computedRB; // only if you actually use prop keys elsewhere
  }
}



     // --- 0) Always enforce the daily loss limit first (has its own once-per-day guard)
     await checkDailyLossLimit(
       allRows,
       settingsForChecks,
       updateSettings,
       userId,
       accountId,
       setSettings
     );

/*        // ✅ Daily goal popup (run after a save so the popup can actually show)
   await checkDailyProfitTarget(
     allRows,
     Number(settingsForChecks["Daily Goal"] || 0),
     settingsForChecks,
     updateSettings,
     userId,
     accountId,
     setSettings
   );*/

     // --- 1) Daily goal popup (skip if account goal is already reached)
   const runningBal  = Number(settingsForChecks["Running Balance"] || 0);
   const accountGoal = Number(settingsForChecks["Account Goal"] || 0);
     const reachedAccountGoal = accountGoal > 0 && runningBal >= accountGoal;

     if (!reachedAccountGoal) {
       const dailyGoal = Number(settingsForChecks["Daily Goal"] || 0);
       await checkDailyProfitTarget(
         allRows,
         dailyGoal,
         settingsForChecks,
         updateSettings,
         userId,
         accountId,
         setSettings
       );
     }

     // --- 2) Over‑Risk warning (prefer basis; fall back to startBal)
     {
       const riskPercent  = Number(settingsForChecks["Risk Percentage"] || 0);
       const tradePnl     = Number(pnl) || 0;
       const startBalForTrade = Number.isFinite(startBal)
         ? Number(startBal)
         : (Number(runningBal) - Number(tradePnl));

       const allowedRisk = startBalForTrade > 0 ? startBalForTrade * (riskPercent / 100) : 0;


       const lossForTrade = Math.max(0, -tradePnl); // positive if this trade is a loss

       if (
         lossForTrade > 0 &&
         allowedRisk > 0 &&
         lossForTrade > allowedRisk &&
         settingsForChecks.OverRiskWarningShownDate !== today
       ) {
         queuePopup("overRiskWarning", { lossForTrade, allowedRisk });
         await updateSettings(userId, accountId, { OverRiskWarningShownDate: today });
         settingsForChecks.OverRiskWarningShownDate = today;
       }
     }

     // --- 3) rule checks (per-day guards inside each helper)
     await checkMaxLossLimitBlown(settingsForChecks, updateSettings, userId, accountId, setSettings);
     await checkAccountGoal(settingsForChecks, updateSettings, userId, accountId, setSettings);

     // --- 4) Will "twoLossAfterGoalWarning" ALSO fire right now? (used to suppress generic 2‑loss popup)
     const twoLossAfterGoalWillTrigger = (() => {
       const dailyGoalAmt = Number(settingsForChecks["Daily Goal"] || 0);
       if (!(dailyGoalAmt > 0)) return false;

       const todayTrades = (allRows || []).filter(
         t => (t?.["Date"] || "").startsWith(today)
       );
       if (todayTrades.length < 3) return false;

       // first time we hit/exceed the daily goal
       let running = 0, hitIdx = -1;
       for (let i = 0; i < todayTrades.length; i++) {
         const r = Number(todayTrades[i]["Actual Reward"] || 0);
         if (Number.isFinite(r)) running += r;
         if (running >= dailyGoalAmt) { hitIdx = i; break; }
       }
       if (hitIdx === -1) return false;

       const after = todayTrades.slice(hitIdx + 1);
       if (after.length < 2) return false;

       // tolerant loss detection
       return (
         isTrueLoss(after[0]["Loss"], after[0]["Actual Reward"]) &&
         isTrueLoss(after[1]["Loss"], after[1]["Actual Reward"])
       );
     })();

     // --- 5) Loss‑streak checks (global last 2/3 trades). Each has a once‑per‑day guard.
     const lastTwo   = (allRows || []).slice(-2);
     const lastThree = (allRows || []).slice(-3);
     const lostBoth     = lastTwo.length   === 2 && lastTwo.every(t => isTrueLoss(t["Loss"], t["Actual Reward"]));
     const lostAllThree = lastThree.length === 3 && lastThree.every(t => isTrueLoss(t["Loss"], t["Actual Reward"]));

     // 5a) Three‑loss warning (once per local day)
     if (lostAllThree && settingsForChecks.ThreeLossWarningShownDate !== today) {
      const message =
        "You've lost 3 trades in a row.\n\nTake a break! \n\nTake some time to evaluate what is really going on. Make sure you are fresh and have a clear mind before taking another trade.\n\nThe Key is to avoid going on tilt and revenge trading.\n\n Trading is 80% Trading Psychology. This may be a good time to watch some psychology videos.  The video below from Psychology of Profits is a good one to start with.";
       queuePopup("threeLossWarning", {
         message,
         videoLink: "https://www.youtube.com/watch?v=s-_oWdFkvkw"
       });
       await updateSettings(userId, accountId, { ThreeLossWarningShownDate: today });
       settingsForChecks.ThreeLossWarningShownDate = today;
     }

     // 5b) Two‑loss warning — SUPPRESSED if twoLossAfterGoal will also fire
     if (
       lostBoth &&
       !lostAllThree &&
       !twoLossAfterGoalWillTrigger &&
       settingsForChecks.TwoLossWarningShownDate !== today
     ) {
      const message = TWO_LOSS_MESSAGE;
      queuePopup("twoLossWarning", { message });
      await updateSettings(userId, accountId, { TwoLossWarningShownDate: today });
      settingsForChecks.TwoLossWarningShownDate = today;

     }

     // --- 6) Two losses after daily goal achieved (has its own once‑per‑day guard + not same‑trade double‑stack)
     await checkTwoLossAfterGoal(
       allRows,
       settingsForChecks,
       updateSettings,
       userId,
       accountId,
       setSettings,
       queuePopup
     );

     // --- 7) Other habit/behavior checks (each has internal guards)
     await checkConsecutiveWins(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);
     await checkMoodVolatility(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);
     await checkLessonsLearnedStreak(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);

     // --- 8) Risk-of-ruin (effective-balance based) & gated day-limits
     await checkRiskOfRuin(lossProjection, settingsForChecks, updateSettings, userId, accountId, setSettings);
     if (settingsForChecks["Max Trades Per Day Toggle"]) {
       await checkOvertrading(
         allRows,
         Number(settingsForChecks["Max Trades Per Day"] || 10),
         settingsForChecks,
         updateSettings,
         userId,
         accountId,
         setSettings
       );
     }
     if (settingsForChecks["Max Loss Trades Per Day Toggle"]) {
       await checkTooManyLosses(
         allRows,
         Number(settingsForChecks["Max Loss Trades Per Day"] || 5),
         settingsForChecks,
         updateSettings,
         userId,
         accountId,
         setSettings
       );
     }

     // --- 9) Drawdown warning (equity reconstructed from P&L)
     await checkDrawdown(
       allRows,
       Number(settingsForChecks["Drawdown Limit %"] || 0),
       settingsForChecks,
       updateSettings,
       userId,
       accountId,
       setSettings
     );

     // --- 10) Single‑trade “big win” popups
     const latestTrade = (allRows && allRows.length) ? allRows[allRows.length - 1] : null;
     if (latestTrade) {
       await checkWin10NoRulesPopup(latestTrade, settingsForChecks, updateSettings, userId, accountId, setSettings, queuePopup);
       await checkWin10WithRulesPopup(latestTrade, settingsForChecks, updateSettings, userId, accountId, setSettings, queuePopup);
     }

     // --- 11) Persist any updated flags into local state
     setSettings(prev => ({ ...settingsForChecks, ...prev })); // prev wins on conflicts
   } catch (err) {
     console.error("runAllPopupChecks error:", err);
   }
 }



 async function handleSaveTrade() {
     const errs = [];
      const sizeNumeric = Number(journal["Size"] ?? calculatedSummary?.lots ?? NaN);
      const isDidNotTake =
        /^\s*did\s+not\s+take\s*$/i.test(journal["Position"] || "");

     
      // Actual Balance: treat null/empty/whitespace as missing
      const abRaw = journal["Actual Balance"];
      const abMissing =
        abRaw == null ||
        String(abRaw).trim() === "" ||
        Number.isNaN(Number(abRaw));

      // Pips: either typed by user, or derived from Entry/Exit Price
      const pipsRaw = journal["Pips"];
      const pipsProvided =
        pipsRaw != null && String(pipsRaw).trim() !== "";
      let pipsNum = Number(pipsRaw);
      let hasTypedPips = pipsProvided && Number.isFinite(pipsNum);

      // If no pips typed, try to derive from Entry/Exit Price for FX pairs
      const pipsFromPriceAbs = !hasTypedPips
        ? computePipsFromPricesAbs(
            settings["Symbol"],
            journal["Entry Price"],
            journal["Exit Price"]
          )
        : null;

      const hasPriceDerivedPips =
        pipsFromPriceAbs != null && Number.isFinite(pipsFromPriceAbs);


      // Prefer typed pips; otherwise use derived pips
      const basePipsAbs = hasTypedPips
        ? Math.abs(pipsNum)
        : hasPriceDerivedPips
          ? Math.abs(pipsFromPriceAbs)
          : 0;

      // Signed pips: Win = +, Loss = –
      const signedPips =
        isDidNotTake || !Number.isFinite(basePipsAbs)
          ? 0
          : (journal["Loss"] ? -basePipsAbs : basePipsAbs);

      // “We have pips” only if they were typed or successfully derived
      const effectiveHasPips = hasTypedPips || hasPriceDerivedPips;

      // Require one of: Actual Balance, Pips, or Entry/Exit Price (unless DNT)
      if (!isDidNotTake && abMissing && !effectiveHasPips) {
        errs.push("- Actual Balance or Pips or Entry/Exit Price");
      }

     if (!journal["Date"]) errs.push("- Date");
     if (!settings["Symbol"]) errs.push("- Symbol");
     //if (!journal["Did I follow my Rules?"]) errs.push("- Did I follow my Rules?");
     if (!(Number.isFinite(sizeNumeric) && sizeNumeric >= 0.01)) {
         errs.push("- Actual Lot Size (must be at least 0.01)");
      }
      {
         const pos = (journal["Position"] || "").trim().toLowerCase();
         const requireWinLoss = pos !== "did not take";
         if (requireWinLoss && !journal["Win"] && !journal["Loss"]) {
           errs.push("- Win/Loss");
         }
       }
     if (!calculatedSummary) errs.push("- Click Calculate before saving");
     if (errs.length) {
       setMissingFields(errs);
       return;
     }

     setSavingTrade(true);
     try {
      const startBal = Number(settings["Running Balance"]) || 0;
      let endBal;
        if (!abMissing) {
          endBal = Number(journal["Actual Balance"]) || 0;
        } else {
          const lotsUsed = Number(journal["Size"] ?? calculatedSummary?.lots ?? 0);
          const pipValue = Number(settings["Pip Value"]) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);
          const pnl = signedPips * pipValue * lotsUsed; 
          endBal = +(startBal + pnl).toFixed(2);
          setJournal(j => ({ ...j, ["Actual Balance"]: endBal })); // keep UI in sync
        }

       const actualRewardDollar = endBal - startBal;

      const currentSettings = { ...settings, "Running Balance": endBal };


       const toSave = { ...journal, Size: sizeNumeric };
       toSave["Potential"] = journal["Potential"];
       toSave["Pips"] = effectiveHasPips ? signedPips : "";

       // ✅ Save Stop Loss and Take Profit into each trade
       toSave["Stop Loss"] = settings["Stop Loss"] || "";
       toSave["Take Profit"] = settings["Take Profit"] || "";
       toSave["Actual Balance"] = Number(endBal.toFixed(2));
       toSave["Stop Loss_Take Profit"] =
         settings["Stop Loss"] && settings["Take Profit"]
           ? `${settings["Stop Loss"]}/${settings["Take Profit"]}`
           : "";

       // ✅ Save calculated risk amount
       const lotsUsed = sizeNumeric;
       // FIX: Safely pull the calculated distance so Price inputs don't inflate risk
       const stopPips = Number(calculatedSummary?.stopAmt ?? settings["Stop Loss"] ?? 0); 
       const pipValue = Number(settings["Pip Value"]) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);
       const actualRiskDollar = Math.max(0, lotsUsed * stopPips * pipValue);
       toSave["Total Risk"] = actualRiskDollar;       // ✅ Save Percent of Balance as a DOLLAR budget (RB × Risk%)
       toSave["Percent of Balance"] = Number(
         (
           (calculatedSummary?.percentOfBalance ?? (
             Number(settings["Running Balance"] || 0) * Number(settings["Risk Percentage"] || 0) / 100
           )) || 0
         ).toFixed(2)
       );


        // ✅ Percent of Balance (trade risk as % of starting balance)
        const baseForPct = Number(settings["Running Balance"]) || 0;
        // toSave["Percent of Balance"] = baseForPct > 0 ? Number(((actualRiskDollar / baseForPct) * 100).toFixed(2)) : 0;

          const didNotTake =
            /^\s*did\s+not\s+take\s*$/i.test(journal["Position"] || "");
        
          if (didNotTake) {
            // Leave blank for DNT
            toSave["Actual Risk to Reward"] = "";
          } else {
            let rrStr = "";
            if (actualRiskDollar > 0) {
              const sign = actualRewardDollar < 0 ? "-" : "";
              const r = Math.abs(actualRewardDollar) / actualRiskDollar;
              const rStr = Number.isInteger(r) ? `${r}` : r.toFixed(2).replace(/\.?0+$/, "");
              rrStr = `${sign}${rStr}:1`;
            }
            toSave["Actual Risk to Reward"] = rrStr;
          }

       toSave["Product"] = settings["Symbol"] || "";
       toSave["Duration"] = getDurationHHMMSS(to24HourTime(journal["Entry Time"]), to24HourTime(journal["Exit Time"]));
       toSave["Trade #"] = await getNextTradeNumber(userId, accountId);
       if (startBal > 0) {
         toSave["Actual Percent Growth"] = ((actualRewardDollar / startBal) * 100).toFixed(2) + "%";
       }
       toSave["Actual Reward"] = (actualRewardDollar || 0).toFixed(2);
       if (!toSave["Date"]) toSave["Date"] = getTodayLocalISO();

       toSave["tickSize"] = getPipSizeForSymbol(settings["Product"]); 
       toSave["assetClass"] = "FOREX"
       toSave["Buying Power"] = endBal;
       toSave["Unsettled Balance"] = 0;
       toSave["Total Equity"] = endBal;
       toSave["Emotional Notes"] = journal["Emotional Notes"] || "";
       toSave["Target Reached"] = journal["Target Reached"] || "";

       await appendTrade(userId, accountId, toSave);
       if (journal.LogLesson && (journal.Lesson || "").trim()) {
         try {
           const textOnly = (journal.Lesson || "").trim();
           await appendLesson(textOnly);   // ✅ single argument only
           // IMPORTANT: Do not use the return value; do not write it back to trade fields.
         } catch (err) {
           console.error("Error appending lesson from CalculateForm:", err);
         }
       }
        const nextSettings = { 
            ...settings, 
            "Running Balance": endBal,
            "Beginning Balance": endBal 
        };
        await updateSettings(userId, accountId, nextSettings);
        setSettings(nextSettings);
        // Ensure Summary reflects the new balance (updates "Risk Budget ($)" and "Equivalent % of Balance")
        await refreshAfterSave(userId, accountId);



             // === Copy Trade (if this account is a Forex leader) ===
             try {
               const delta = +(endBal - startBal).toFixed(2);
               const me = await getSettings(userId, accountId);
               if (me?.CopyTrade?.role === "leader" || me?.settings?.CopyTrade?.role === "leader") {
                 const accountType = "forex";
                 const { copyTradeToFollowers } = await import("../services/CopyTradeService");
                 await copyTradeToFollowers({
                   userId,
                   leaderAccountId: accountId,
                   leaderTrade: toSave,
                   delta,
                   accountType
                 });
               }
             } catch (e) {
               console.error("[CopyTrade] forex retail copy failed", e);
             }
       const allRows = await fetchJournalRows(userId, accountId);
       const rowsWithThisTrade = allRows.some(r => String(r["Trade #"]) === String(toSave["Trade #"]))
         ? allRows
         : [...allRows, toSave];
       
       await updateDailyProgress(rowsWithThisTrade);
       // (Your projection logic remains here...)




       // Defer the rest of the popup checks
       setPostSaveCheckInfo({
         rows: rowsWithThisTrade,
         settings: currentSettings,
         projection: lossProjection,
         startBal: startBal,
         pnl: actualRewardDollar
       });

       const defTf = String(settings?.["Default Timeframe"] || "").trim();

       setJournal({
         ...defaultJournal,
         Size: calculatedSummary?.lots ?? "",
         Timeframe: defTf, // ✅ keep default after save
         Session: String(settings?.["Default Session"] || "").trim(),
         "Entry Strategy": String(settings?.["Default Entry Strategy"] || "").trim(),
         "Exit Strategy": String(settings?.["Default Exit Strategy"] || "").trim(),
         "Emotional Notes": "",
       });
       setShowTradeSaved(true);

     } catch (err) {
       alert("Failed to save journal entry.");
       console.error("handleSaveTrade error:", err);
     } finally {
       setSavingTrade(false);
     }
   }

function highlightHundredPercent(line) {
  if (!line) return null;
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">
        100%
      </span>
    ) : (
     <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}

async function handleCalculate() {
     popupsAllowedRef.current = false; 
    setSavingSettings(true);
    setHasCalculated(true);
    let freshSettings = { ...settings };
    const today = getTodayLocalISO();
    try {
      await updateSettings(userId, accountId, settings);
      freshSettings = await getSettings(userId, accountId);

       const {
         Symbol,
         "Running Balance": rb,
         "Risk Percentage": rp,
         "Max Lot Size": maxL,
         "Min Lot Size": minL,
         "Account Goal": goal,
         "Pip Value": pvRaw
       } = freshSettings;

      const pv = Number(pvRaw) || getPipValue(Symbol, 1, settings["Account Currency"]);
      const entryPrice = Number(freshSettings["Entry Price"] || 0);

      // --- NEW: DYNAMIC EXIT LOGIC ---
      const slMode = freshSettings["Stop Loss Mode"] || "Pips";
      const tpMode = freshSettings["Take Profit Mode"] || "Pips";
      const rawSL  = Number(freshSettings["Stop Loss"] || 0);
      const rawTP  = Number(freshSettings["Take Profit"] || 0);
      const pipSize = getPipSizeForSymbol(Symbol) || 0.0001;

      let sl = rawSL;
      if (slMode === "Price" && entryPrice > 0) {
          sl = pipSize > 0 ? Math.abs(entryPrice - rawSL) / pipSize : 0;
      }
      
      let tp = rawTP;
      if (tpMode === "Price" && entryPrice > 0) {
          tp = pipSize > 0 ? Math.abs(rawTP - entryPrice) / pipSize : 0;
      }
      // -------------------------------

      // --- NEW: compute budget from % or $ mode
      const baseBal = Number(rb) || 0;
      const riskBudget = riskBudgetDollar(freshSettings, baseBal);
      const riskPercentDisplay = riskPercentFromDollar(freshSettings, baseBal);

      let riskRewardRatio = "";
      if (tp && sl) {
        const rr = tp / sl;
        riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
      }

     // Size using the active risk mode ($ converted to % when needed)
     const lots = getSuggestedLotSize(baseBal, riskPercentDisplay, sl, pv, minL, maxL);
      const totalRisk = (lots > 0 ? lots : minL) * sl * pv;
      const targetReward = (lots > 0 ? lots : minL) * pv * tp;
      const { perLot, label } = getUnitsMeta(Symbol);
      const units = lots > 0 ? lots * perLot : 0;
      const riskMode   = getRiskMode(settings);                  // "%" or "$"
const riskDollar = Number(settings["Risk Dollar"] || 0);   // constant $/trade


      // Pre-fill typed size if empty
      setJournal(prev => (prev.Size === "" || prev.Size == null ? { ...prev, Size: lots } : prev));

      setCalculatedSummary({
        riskRewardRatio,
        percentOfBalance: riskBudget,      // $ budget
        riskPercent: riskPercentDisplay,   // % label
        stopLoss: sl,
        takeProfit: tp,
        stopAmt: sl,    
        tpAmt: tp,
        lots,
        minLot: minL,
        totalRisk,
        targetReward,
        units,
        unitsLabel: label,
        riskMode, riskDollar,
        pipValue: pv
      });

      // risk warning compares against $ budget
      if (Number(totalRisk) > Number(riskBudget)) setShowRiskWarningPopup(true);
      else setShowRiskWarningPopup(false);


      setWinProjection(
        getCompoundingProjection({
          balance: rb,
          goal,
          riskPercent: rp,
          stopLoss: sl,
          takeProfit: tp,
          maxLot: maxL,
          minLot: minL,
          pipValue: pv,
          riskMode, riskDollar
        })
      );

       setLossProjection(
         getLossProjection({
           balance: rb, stopLoss: sl, pipValue: pv, minLot: minL, maxLot: maxL, riskPercent: rp,
           riskMode, riskDollar
         })
       );

     

     if (Number(totalRisk) > Number(riskBudget)) setShowRiskWarningPopup(true);
     else setShowRiskWarningPopup(false);
    } catch (err) {
      alert("Failed to calculate/save settings");
      console.error("Settings save failed:", err);
    }
    setSavingSettings(false);
  }

  // Load settings on mount
  useEffect(() => {
    const load = async () => {
      try {
        hasRunInitialAutoCalc.current = false;
       const raw = await getSettings(userId, accountId);
       const s   = { ...defaultSettings, ...raw };
       // normalize risk mode to "%" | "$"
       const m = String(s["Risk Mode"] || "%").trim().toLowerCase();
       s["Risk Mode"] = (m === "$" || m.startsWith("d")) ? "$" : "%";
       // pull dollar value from any legacy aliases
       if (s["Risk Mode"] === "$") {
         const dollar =
           Number(s["Risk Dollar"]) ??
           Number(s["Risk Amount"]) ??
           Number(s["Risk Dollar Amount"]) ?? 0;
         s["Risk Dollar"] = Number.isFinite(dollar) ? dollar : 0;
       }
       // ensure Pip Value present on first paint
       if (!(Number(s["Pip Value"]) > 0) && s["Symbol"]) {
         s["Pip Value"] = getPipValue(s["Symbol"], 1, s["Account Currency"]);
       }
       setSettings(s);
        loadedFromServer.current = true;
        // ... keep the rest (fetchJournalRows, strategies, etc.)
      } catch (err) {
        console.error("Error loading settings:", err);
      }
    };
    load();
  }, [userId, accountId]);

  // Progress bar % to goal
  useEffect(() => {
    const initial = Number(settings["Initial Balance"] || 0);
    const goal = Number(settings["Account Goal"] || 0);
    const running = Number(settings["Running Balance"] || 0);
    let pct = 0;
    if (goal > initial) pct = ((running - initial) / (goal - initial)) * 100;
    setProgress(Math.max(0, Math.min(100, pct)));
  }, [settings["Initial Balance"], settings["Account Goal"], settings["Running Balance"]]);



  // Render helpers
  // Safely normalize the suggested lot size for display + math
  const suggestedLotsNum = Number(calculatedSummary?.lots);
  const suggestedLots = Number.isFinite(suggestedLotsNum) && suggestedLotsNum > 0 ? suggestedLotsNum : 0;

  // Summary & risk calcs ALWAYS use suggested lot size
  const lotsForRisk = suggestedLots > 0 ? suggestedLots : 0;
  const isZeroLot = lotsForRisk === 0;

  // Use the same lot basis for the maximum stop calculation; 0 means "not applicable"
  const maxStopLossValue = (calculatedSummary && lotsForRisk > 0)
    ? Math.floor(
        Number(calculatedSummary.percentOfBalance || 0) /
        (lotsForRisk * Number(pipValue || 0))
      )
    : 0;

  const isTightStop = maxStopLossValue > 0 && maxStopLossValue < 5;



  // Projections calc helpers
  function getCompoundingProjection({
    balance, goal, riskPercent, stopLoss, takeProfit, maxLot, minLot, pipValue,
    riskMode = "%", riskDollar = 0
  }) {
    let trades = [];
    let currentBalance = Number(balance) || 0;
    const perLotRisk = Number(stopLoss) * Number(pipValue);

    while (currentBalance < Number(goal) && trades.length < 10000) {
      // Per‑trade risk budget
      const budgetDollar = riskMode === "$"
        ? Math.max(0, Number(riskDollar) || 0)
        : (currentBalance * Number(riskPercent || 0)) / 100;

      // Lot sizing from budget
      let lots = perLotRisk > 0 ? budgetDollar / perLotRisk : 0;
      lots = Math.floor(Math.max(minLot, Math.min(maxLot, lots)) / 0.01) * 0.01;
      if (lots === 0) lots = minLot;

      const totalRisk   = lots * perLotRisk;
      const tradeProfit = lots * Number(pipValue) * Number(takeProfit);

      trades.push({
        trade: trades.length + 1,
        balance: currentBalance,
        riskDollar: budgetDollar,
        lots,
        totalRisk,
        tradeProfit,
        nextBalance: currentBalance + tradeProfit,
        underfunded: totalRisk > budgetDollar // TRUE when min lot already exceeds the risk budget
      });

      currentBalance += tradeProfit;
    }

    return { trades, tradeCount: trades.length };
  }

function getUnitsMeta(symbol) {
  const s = (symbol || "").toUpperCase();

  // Metals
  if (s.startsWith("XAU")) return { perLot: 100, label: "oz" };
  if (s.startsWith("XAG")) return { perLot: 5000, label: "oz" };

  // ✅ Indices (CFDs) — show 1 lot = 1 unit
  const INDEX_SYMBOLS = [
    "US30", "NAS100", "SPX500", "US2000",
    "GER40", "UK100", "FRA40", "JP225", "HK50", "AUS200"
  ];
  if (INDEX_SYMBOLS.includes(s)) return { perLot: 1, label: "units" };

  // Forex pairs
  return { perLot: 100000, label: "units" };
}


// replace the whole function
function getLossProjection({
  balance, stopLoss, pipValue, minLot, maxLot, riskPercent,
  riskMode = "%", riskDollar = 0
}) {
  let trades = [];
  let currentBalance = Number(balance) || 0;
  const perLotRisk = Number(stopLoss) * Number(pipValue);
  if (perLotRisk <= 0) return { trades: [], tradeCount: 0, suggestedLots: 0 };

  while (currentBalance > 0 && trades.length < 10000) {
    // use $ budget when toggle = "$"
    const budgetDollar = riskMode === "$"
      ? Math.max(0, Number(riskDollar) || 0)
      : (currentBalance * Number(riskPercent || 0)) / 100;

    let calcLots = perLotRisk > 0 ? budgetDollar / perLotRisk : 0;
    // clamp + 0.01 lot granularity
    calcLots = Math.floor(Math.max(minLot, Math.min(maxLot, calcLots)) / 0.01) * 0.01;
    if (!Number.isFinite(calcLots) || calcLots <= 0) calcLots = minLot;

    const lossPerTrade = perLotRisk * calcLots;
    if (lossPerTrade <= 0) break;

    const balanceAfter = Math.max(currentBalance - lossPerTrade, 0);

    trades.push({
      trade: trades.length + 1,
      balanceBefore: currentBalance,
      lots: calcLots,
      loss: lossPerTrade,
      balanceAfter
    });

    currentBalance = balanceAfter;
  }

  return { trades, tradeCount: trades.length, suggestedLots: 0 };
}

  // 🔁 Always refresh progress bars whenever balances or trades change
  useEffect(() => {
    if (!userId || !accountId) return;

    // Re-run daily PnL calculation
    updateDailyProgress();

    console.log("[DailyProgress] Refreshed due to balance or trade changes");
  }, [
    userId,
    accountId,
    settings["Running Balance"],
    calculatedSummary,
  ]);
  const isDidNotTake =
    (journal["Position"] || "").trim().toLowerCase() === "did not take";

  return (
    <div className="space-y-8 max-w-5xl mx-auto py-10">
      {/* --- Account Settings Card --- */}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 mb-4 animate-fadeIn">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
          {/* Wrap the Icon and Title in their own flex container */}
          <div className="flex items-center gap-2">
            <IconGoal />
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">Trade Setup (Backtesting)</h2>
          </div>

          {/* NEW STRATEGY CHECKLIST BUTTON */}
          <button 
            onClick={() => setShowStrategyModal(true)}
            className="ml-auto md:ml-0 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 font-bold px-4 py-2 rounded-lg transition"
          >
            <FaClipboardList /> Strategy Checklist
          </button>
        </div>

        <div className="space-y-6">
          
          {/* --- COLLAPSIBLE BALANCES SECTION --- */}
          <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span>Account Cash Status</span>
                <span className="text-sm font-normal text-gray-500">
                  (Running Balance: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"])}</span>)
                </span>
              </div>
              <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
              <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
            </summary>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
                <input 
                  type="text" 
                  inputMode="decimal" 
                  value={settings["Account Goal"] ?? ""} 
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.-]/g, "");
                    handleSettingChange("Account Goal", raw);
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    if (!isNaN(n)) handleSettingChange("Account Goal", n.toFixed(2));
                  }}
                  className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400 bg-white" 
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Running Balance</label>
                <input 
                  type="text" 
                  inputMode="decimal" 
                  value={settings["Running Balance"] ?? ""} 
                  onChange={(e) => {
                    // Allow user to freely type numbers, decimals, and minus signs
                    const raw = e.target.value.replace(/[^0-9.-]/g, "");
                    setSettings((s) => ({ ...s, "Running Balance": raw }));
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    if (!isNaN(n)) {
                      const formatted = n.toFixed(2);
                      // 1. Update the state with the formatted value
                      setSettings((s) => ({ ...s, "Running Balance": formatted }));
                      
                      // 2. Auto-run the background calculation with the new value
                      if (typeof runSummaryOnly === "function") {
                        runSummaryOnly({ "Running Balance": Number(formatted) });
                      }
                    }
                  }}
                  className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400 bg-white font-bold" 
                  placeholder="0.00"
                />
              </div>
            </div>
          </details>

{/* --- STEP 1: THE SETUP --- */}
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
                  Symbol<span className="text-red-500 text-lg">*</span>
                </label>
                <select 
                  value={settings["Symbol"] || ""} 
                  onChange={e => handleSettingChange("Symbol", e.target.value)} 
                  className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white bg-gray-50 focus:ring-2 focus:ring-blue-400 transition ${
                    missingFields.includes("Symbol") ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                  }`}
                >
                  <option value="">Select Symbol</option>
                  {favoriteForexOptions.length > 0 && (
                    <optgroup label="♥ Favorites">
                      {favoriteForexOptions.map(([v, label]) => (
                        <option key={v} value={v}>{label || v}</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="All Forex">
                    {nonFavoriteSymbols.map(([v, label]) => (
                      <option key={v} value={v}>{label || v}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Added Entry Price */}
            <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
              Entry Price{(settings["Stop Loss Mode"] === "Price" || settings["Take Profit Mode"] === "Price") ? <span className="text-red-500 text-sm">* (Required)</span> : <span className="font-normal text-sm text-gray-500"> (Optional)</span>}
            </label>
            <input 
              type="number" 
              placeholder="e.g. 4500.25" 
              value={settings["Entry Price"]||""} 
              onChange={e=>handleSettingChange("Entry Price",e.target.value)} 
              className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400" 
            />
          </div>
            </div>
          </div>

          {/* --- STEP 2: EXITS --- */}
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
            <div className="grid grid-cols-2 gap-6">
              
              {/* Stop Loss Group */}
              <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-1">
                    <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
                    <Tooltip 
                      variant="blue" 
                      backdrop 
                      content={
                        <div className="space-y-3 p-1">
                          <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                            Tip
                          </div>
                          <p className="text-[15px] leading-relaxed text-gray-100">
                            In fast markets, you don’t need an exact stop loss price. Use a reasonable estimate based on your strategy (example: <span className="font-bold text-white">10–20 pips</span> on GBPUSD, <span className="font-bold text-white">20–40 pips</span> on XAUUSD).
                          </p>
                          <p className="text-[15px] font-bold text-blue-300">
                            Backtesting your strategy is an excellent way to determine that number.
                          </p>
                        </div>
                      }
                    >
                      <span className="cursor-help text-[10px] text-blue-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                    </Tooltip>
                  </div>
                  <select 
                    value={settings["Stop Loss Mode"] || "Pips"} 
                    onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                    className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                  >
                    <option value="Pips">by Pips</option>
                    <option value="Price">by Price</option>
                  </select>
                </div>
                <input 
                  type="number" 
                  placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : "Pips"}
                  value={settings["Stop Loss"] || ""} 
                  onChange={e => handleSettingChange("Stop Loss", e.target.value)} 
                  className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400 bg-white"
                />
                {/* Dynamic Helper Text */}
                {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
                  <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                    {settings["Stop Loss Mode"] === "Price" 
                      ? (() => {
                          const pipSize = getPipSizeForSymbol(settings["Symbol"]) || 0.0001;
                          const distPips = Math.abs(settings["Entry Price"] - settings["Stop Loss"]) / pipSize;
                          const isIdx = isIndexSymbol(settings["Symbol"]);
                          const distTicks = isIdx ? distPips * 100 : distPips * 10;
                          return `Est. Distance: ${Math.round(distPips * 100) / 100} ${isIdx ? 'Points' : 'Pips'} / ${Math.round(distTicks * 100) / 100} Ticks`;
                        })()
                      : `Est. Stop Price: ${(Number(settings["Entry Price"]) - (Number(settings["Stop Loss"]) * (getPipSizeForSymbol(settings["Symbol"]) || 0.0001))).toFixed(isIndexSymbol(settings["Symbol"]) ? 2 : 5)}`}
                  </p>
                )}              </div>

              {/* Take Profit Group */}
              <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
                <div className="flex justify-between items-center mb-2">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Take Profit</label>
                  <select 
                    value={settings["Take Profit Mode"] || "Pips"} 
                    onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                    className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                  >
                    <option value="Pips">by Pips</option>
                    <option value="Price">by Price</option>
                  </select>
                </div>
                <input 
                  type="number" 
                  placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : "Pips"}
                  value={settings["Take Profit"] || ""} 
                  onChange={e => handleSettingChange("Take Profit", e.target.value)} 
                  className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400 bg-white"
                />
              {/* Dynamic Helper Text */}
                {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
                  <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                    {settings["Take Profit Mode"] === "Price" 
                      ? (() => {
                          const pipSize = getPipSizeForSymbol(settings["Symbol"]) || 0.0001;
                          const distPips = Math.abs(settings["Take Profit"] - settings["Entry Price"]) / pipSize;
                          const isIdx = isIndexSymbol(settings["Symbol"]);
                          const distTicks = isIdx ? distPips * 100 : distPips * 10;
                          return `Est. Distance: ${Math.round(distPips * 100) / 100} ${isIdx ? 'Points' : 'Pips'} / ${Math.round(distTicks * 100) / 100} Ticks`;
                        })()
                      : `Est. Target Price: ${(Number(settings["Entry Price"]) + (Number(settings["Take Profit"]) * (getPipSizeForSymbol(settings["Symbol"]) || 0.0001))).toFixed(isIndexSymbol(settings["Symbol"]) ? 2 : 5)}`}
                  </p>
                )}
              </div>

            </div>
          </div>

          {/* --- STEP 3: RISK PARAMETERS --- */}
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Risk Parameters</h3>
            <div>
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget<span className="text-red-500 text-lg">*</span></label>
                </div>
                <select 
                  value={(String(settings["Risk Mode"] || "%") === "%" || String(settings["Risk Mode"]) === "Percent") ? "%" : "$"} 
                  onChange={e => handleSettingChange("Risk Mode", e.target.value)} 
                  className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="$">$ Amount</option>
                  <option value="%">% of Balance</option>
                </select>
              </div>
              
              {(String(settings["Risk Mode"] || "%") === "%" || String(settings["Risk Mode"]) === "Percent") ? (
                <input
                  type="text"
                  inputMode="decimal"
                  value={settings["Risk Percentage"] ?? ""}
                  onChange={e => handleSettingChange("Risk Percentage", e.target.value)}
                  onBlur={e => handleSettingChange("Risk Percentage", commitNumberOrEmpty(e.target.value))}
                  className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400 bg-white"
                  placeholder="Risk %"
                />
              ) : (
                <input
                  type="text"
                  inputMode="numeric"
                  value={settings["Risk Dollar"] ?? ""}
                  onChange={e => handleSettingChange("Risk Dollar", e.target.value)}
                  onBlur={e => handleSettingChange("Risk Dollar", commitNumberOrEmpty(e.target.value))}
                  className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400 bg-white"
                  placeholder="Risk $"
                />
              )}
            </div>
          </div>

          {/* --- PROJECTIONS ROW --- */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <div>
              <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
              <input 
                type="number" 
                value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} 
                readOnly 
                disabled 
                className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" 
              />
            </div>
            <div className="flex items-end">
              <button 
                type="button" 
                className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`} 
                onClick={() => setProjectionOpen(true)} 
                disabled={!calculatedSummary}
              >
                Win Projections
              </button>
            </div>
            <div className="flex items-end">
              <button 
                type="button" 
                className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`} 
                onClick={() => setLossProjectionOpen(true)} 
                disabled={!calculatedSummary}
              >
                Loss Projections
              </button>
            </div>
          </div>

        </div>


        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />










  


        {/* Progress Bar */}
        <div className="mt-8 mb-2">
          <div className="flex items-center gap-4">
            <span className="font-bold text-gray-800 dark:text-gray-100">Goal Progress</span>
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-3 rounded-lg relative overflow-hidden">
              <div className="bg-green-500 h-3 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{Math.round(progress)}%</span>
          </div>
        </div>

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

        {/* Summary */}
        <div className="max-w-lg mx-auto border rounded-xl bg-gray-50 dark:bg-gray-800 p-4 mt-4">
          <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white"><IconGoal />Summary</h3>
          {calculatedSummary ? (
            <div>
              <SummaryRow label="Reward to Risk Ratio" value={calculatedSummary.riskRewardRatio} />
              {String(settings["Risk Mode"] || "%") === "$" ? (
                <>
                  <SummaryRow
                    label="Risk Budget ($)"
                    value={formatCurrency(calculatedSummary.percentOfBalance, settings["Account Currency"])}
                  />
                  <SummaryRow
                    label="Equivalent % of Balance"
                    value={`${Number(calculatedSummary.riskPercent || 0).toFixed(2)}%`}
                  />
                </>
              ) : (
                <SummaryRow
                  label={`${settings["Risk Percentage"]}% of Balance`}
                  value={formatCurrency(calculatedSummary.percentOfBalance, settings["Account Currency"])}
                />
              )}

            {/* --- NEW DYNAMIC STOP LOSS / TAKE PROFIT ROWS --- */}
              {(() => {
                const isIdx = isIndexSymbol(settings["Symbol"]);
                const baseUnit = isIdx ? "points" : "pips";
                
                // Fetch base values in pips/points
                const slVal = Math.round(Number(calculatedSummary.stopAmt || 0) * 100) / 100;
                const tpVal = Math.round(Number(calculatedSummary.tpAmt || 0) * 100) / 100;
                
                // Calculate Ticks based on Asset Type
                const slTicks = isIdx ? Math.round(slVal * 100) : Math.round(slVal * 10 * 100) / 100;
                const tpTicks = isIdx ? Math.round(tpVal * 100) : Math.round(tpVal * 10 * 100) / 100;

                return (
                  <>
                    <SummaryRow 
                      label="Est. Stop Loss" 
                      value={slVal > 0 ? `${slVal} ${baseUnit} (${slTicks} ticks)` : "–"} 
                    />
                    <SummaryRow 
                      label="Est. Take Profit" 
                      value={tpVal > 0 ? `${tpVal} ${baseUnit} (${tpTicks} ticks)` : "–"} 
                    />
                  </>
                );
              })()}
              
              <SummaryRow
                label={
                  <span className="inline-flex items-center gap-2">
                    Total Risk
                    {calculatedSummary && (
                      <Tooltip
                        variant={(() => {
                          const suggestedLots = Number(calculatedSummary?.lots || 0);
                          const lotsForRisk   = suggestedLots > 0 ? suggestedLots : 0;

                          const stopPips = Number(
                            calculatedSummary?.stopAmt ?? // FIX
                            settings["Stop Loss"] ?? 0
                          );

                          const pipVal = Number(
                            // prefer the already-derived render helper if in scope
                            typeof pipValue !== "undefined" ? pipValue
                            : (settings["Pip Value"] || 0)
                          ) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);

                          const budgetDollar = Number(
                            calculatedSummary?.percentOfBalance
                            ?? riskBudgetDollar(settings, Number(settings["Running Balance"] || 0))
                          );

                          if (!(lotsForRisk > 0)) return "neutral";
                          const riskUsed = lotsForRisk * stopPips * pipVal;
                          return riskUsed <= budgetDollar ? "blue" : "red";
                        })()}
                       content={(() => {
                          const currency      = settings["Account Currency"] || "USD";
                          const suggestedLots = Number(calculatedSummary?.lots || 0);
                          const lotsForRisk   = suggestedLots > 0 ? suggestedLots : 0;

                          // Look directly at the dynamically calculated distance (stopAmt)
                          const stopPips = Number(
                            calculatedSummary?.stopAmt ??
                            settings["Stop Loss"] ?? 0
                          );

                          const pipVal = Number(
                            typeof pipValue !== "undefined" ? pipValue
                            : (settings["Pip Value"] || 0)
                          ) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);

                          const budgetDollar = Number(
                            calculatedSummary?.percentOfBalance
                            ?? riskBudgetDollar(settings, Number(settings["Running Balance"] || 0))
                          );

                          const riskUsed     = lotsForRisk * stopPips * pipVal;
                          const equalsBudget = Math.abs(riskUsed - budgetDollar) < 0.01;
                          
                          // --- NEW: Dynamic Unit Formatting ---
                          const isIdx = isIndexSymbol(settings["Symbol"]);
                          const unitName = isIdx ? "points" : "pips";
                          const valueNameCap = isIdx ? "Point value" : "Pip value";

                          return (
                            <div className={`space-y-2 ${!(lotsForRisk > 0) ? "text-red-600 dark:text-red-400" : ""}`}>
                              <div className={
                                "text-sm uppercase tracking-wide font-bold " +
                                (!(lotsForRisk > 0) ? "text-red-700 dark:text-red-500" : "text-blue-700 dark:text-blue-400")
                              }>
                                How we calculate total risk
                              </div>

                              {/* Plain-English formula */}
                              <div className="font-sans text-[16px]">
                                Stop Loss ({unitName}) × Lots × {valueNameCap} per Lot
                              </div>

                              {/* With actual values */}
                              <div className="font-sans text-[16px]">
                                {stopPips} {unitName} × {lotsForRisk.toFixed(2)} lot{lotsForRisk === 1 ? "" : "s"} × {pipVal} ={" "}
                                <strong>{formatCurrency(riskUsed, currency)}</strong>
                              </div>

                              {/* Pip/Point value note */}
                              <div className="text-[13px] opacity-80">
                                {valueNameCap} used: <strong>{formatCurrency(pipVal, currency)}</strong> per 1.00 lot
                                {settings["Symbol"] ? <> on <strong>{String(settings["Symbol"]).toUpperCase()}</strong></> : null}
                              </div>

                              {/* --- NEW: Simplified Explanation --- */}
                              <div className="text-[12px] opacity-60 italic mt-0.5 leading-snug">
                                *This is a standard estimate. Actual values may vary slightly depending on your broker and live exchange rates.
                              </div>

                              {/* If no tradable lot */}
                              {!(lotsForRisk > 0) && (
                                <p className="text-[13px] mt-2">
                                  Suggested/typed lot size is <span className="font-semibold">0.00</span>, so no trade can be placed.
                                </p>
                              )}

                              {/* Budget */}
                              <div className="font-sans text-[16px] mt-2">
                                Risk budget: <strong>{formatCurrency(budgetDollar, currency)}</strong>{" "}
                                <span className="opacity-75">
                                  ({Number(calculatedSummary?.riskPercent || 0).toFixed(2)}% of balance)
                                </span>
                              </div>

                              {/* Verdict */}
                              {lotsForRisk > 0 && (
                                <div
                                  className={`mt-1 text-[16px] font-semibold ${
                                    equalsBudget
                                      ? "text-green-300"
                                      : (riskUsed <= budgetDollar ? "text-green-300" : "text-red-400 dark:text-red-400")
                                  }`}
                                >
                                  {equalsBudget
                                    ? "✓ Total risk = risk budget"
                                    : riskUsed <= budgetDollar
                                      ? "✓ Total risk ≤ risk budget"
                                      : "✕ Total risk > risk budget"}
                                  
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      >
                        <span
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold"
                          aria-label="Show how total risk is calculated"
                        >
                          ?
                        </span>
                      </Tooltip>
                    )}
                  </span>
                }
                value={(() => {
                  const suggestedLots = Number(calculatedSummary?.lots || 0);
                  const lotsForRisk   = suggestedLots > 0 ? suggestedLots : 0;

                  const stopPips = Number(
                    calculatedSummary?.stopLossPips ??
                    calculatedSummary?.stopLoss ??
                    settings["Stop Loss"] ?? 0
                  );

                  const pipVal = Number(
                    typeof pipValue !== "undefined" ? pipValue
                    : (settings["Pip Value"] || 0)
                  ) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);

                  const risk = lotsForRisk > 0 ? lotsForRisk * stopPips * pipVal : 0;
                  return lotsForRisk > 0
                    ? formatCurrency(risk, settings["Account Currency"])
                    : "";
                })()}
                red={(() => {
                  const suggestedLots = Number(calculatedSummary?.lots || 0);
                  const lotsForRisk   = suggestedLots > 0 ? suggestedLots : 0;

                  if (!(lotsForRisk > 0)) return true; // highlight when no trade is possible

                  const stopPips = Number(
                    calculatedSummary?.stopLossPips ??
                    calculatedSummary?.stopLoss ??
                    settings["Stop Loss"] ?? 0
                  );

                  const pipVal = Number(
                    typeof pipValue !== "undefined" ? pipValue
                    : (settings["Pip Value"] || 0)
                  ) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);

                  const risk = lotsForRisk * stopPips * pipVal;

                  const budgetDollar = Number(
                    calculatedSummary?.percentOfBalance
                    ?? riskBudgetDollar(settings, Number(settings["Running Balance"] || 0))
                  );

                  return risk > budgetDollar;
                })()}
              />


              <SummaryRow label="Target Reward" value={formatCurrency(calculatedSummary.targetReward, settings["Account Currency"])} />
              {/* --- NEW DYNAMIC MAXIMUM STOP LOSS ROW --- */}
              {(() => {
                 const isIdx = isIndexSymbol(settings["Symbol"]);
                 const baseUnit = isIdx ? "points" : "pips";
                 return (
                   <SummaryRow
                     label="Maximum Stop Loss"
                     value={maxStopLossValue > 0 ? `${maxStopLossValue} ${baseUnit} (${maxStopLossValue * 10} ticks)` : ""}
                     red={isTightStop}
                   />
                 );
              })()}              

              {/* Position Size (units) */}
              <SummaryRow
                label={`Position Size (${calculatedSummary.unitsLabel})`}
                value={
                  calculatedSummary.lots > 0
                    ? (() => {
                        const rawUnits = Number(calculatedSummary.units) || 0;
                        // FIX: Remove JPY division, format exactly like other files
                        return Number(rawUnits).toLocaleString(undefined, { maximumFractionDigits: 2 });
                      })()
                    : ""
                }
              />

               <div
                 className={
                   isZeroLot
                     ? "rounded-md px-2 py-1 bg-red-50 dark:bg-red-900/40"
                     : "rounded-md px-2 py-1 bg-blue-300 dark:bg-blue-400/30"
                 }
               >
                 <SummaryRow
                   label={<span className="text-gray-600 dark:text-gray-300">Suggested Lot Size</span>}
                   value={
                     isZeroLot
                       ? <span className="text-red-600 font-bold">0</span>
                       : <span className="text-gray-600 dark:text-gray-300 font-semibold">
                           {suggestedLots.toFixed(2)}
                         </span>
                   }
                   red={isZeroLot}
                 />
               </div>

              {isTightStop && (
                <div className="text-xs text-red-500 mt-1 font-semibold">
                  Warning: Maximum Stop Loss is extremely tight and may not be viable in volatile markets.
                </div>
              )}
            </div>
          ) : (
            <div className="italic text-gray-500">No calculations yet. Fill out the form above and click Calculate.</div>
          )}
        </div>

        {/* Calculate */}
        <div className="flex gap-4 justify-end mt-6">
          <button
            type="button"
            onClick={() => handleCalculate(true)}
            disabled={savingSettings || !settings["Symbol"]}
            aria-disabled={savingSettings || !settings["Symbol"]}
            title={!settings["Symbol"] ? "Select a symbol first" : undefined}
            className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-800 dark:to-blue-500 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-500 dark:hover:from-blue-900 dark:hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSettings ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>

{/* Journal Entry - Logical Grouping Layout (Backtesting) */}
      {!settings["Calculator Only"] && (
<div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
  
  {/* Header */}
  <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
    <IconJournal />
    <div>
      <h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Log your backtest results below. <span className="text-red-500">* denotes required</span>
      </p>
    </div>
  </div>

  <div className="space-y-8"> 

    {/* SECTION 1: SETUP & CONTEXT */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">
        1. Setup & Context
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Date */}
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500 text-lg">*</span></label>
          <input 
            type="date" 
            value={journal["Date"] || getTodayISO()} 
            onChange={e => handleJournalChange("Date", e.target.value)} 
            className={`w-full px-4 py-2 h-[42px] rounded-lg border bg-gray-50 dark:bg-gray-700 dark:text-white transition focus:ring-2 focus:ring-blue-400 ${
              missingFields.includes("Date") ? "border-red-500" : "border-gray-200 dark:border-gray-700"
            }`}
          />
        </div>
        
        {/* Session */}
        {!settings["Hide Session"] && (
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label>
          <select 
            value={journal["Session"]} 
            onChange={e => handleJournalChange("Session", e.target.value)} 
            className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
          >
            <option value="">Select</option>
            {SESSION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        )}

        {/* Timeframe */}
        <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label>
           <input 
             type="text" 
             list="timeframe-options-fx" 
             value={journal["Timeframe"]} 
             onChange={e => handleJournalChange("Timeframe", e.target.value)} 
             placeholder="e.g. 5m"
             className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
           />
           <datalist id="timeframe-options-fx">
              {TIMEFRAME_OPTIONS_FX.map(tf => <option key={tf} value={tf} />)}
           </datalist>
        </div>

        {/* Position */}
        <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label>
           <select 
             value={journal["Position"]} 
             onChange={e => {
                const val = e.target.value;
                handleJournalChange("Position", val);
                if (val.trim().toLowerCase() === "did not take") {
                  setJournal(j => ({ ...j, Win: false, Loss: false }));
                }
             }} 
             className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
           >
            <option value="">Select</option>
            {POSITION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>
    </div>

    {/* SECTION 2: EXECUTION */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">
        2. Execution
      </h4>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Entry Group */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label>
          <div className="space-y-4">
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
               <div className="relative flex items-center">
                  <input 
                    id="entryTime" 
                    type="time" 
                    ref={entryTimeRef} 
                    value={to24HourTime(journal["Entry Time"])} 
                    onChange={e => {
                      const rawTime24 = e.target.value;
                      setJournal(prev => {
                        const nextState = { ...prev, "Entry Time": toAmPm(rawTime24) };
                        
                        // Auto-populate Session only if it is currently empty
                        if (!prev.Session || prev.Session === "") {
                          const calculatedSession = getSessionFromTime(rawTime24);
                          if (calculatedSession) {
                            nextState.Session = calculatedSession;
                          }
                        }
                        
                        return nextState;
                      });
                    }} 
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" 
                  />
                  <FaRegClock
                    className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                    onClick={() => entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()}
                  />
               </div>
             </div>

             {!settings["Hide Entry Price"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
               <input 
                 type="number" 
                 placeholder="0.00000" 
                 value={journal["Entry Price"] ?? ""}
                 onChange={e => handleJournalChange("Entry Price", e.target.value)}
                 className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
               />
             </div>
             )}

             {!settings["Hide Entry Strategy"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
               <input 
                 type="text" 
                 list="entry-strategy-options"
                 value={journal["Entry Strategy"]} 
                 onChange={e => handleJournalChange("Entry Strategy", e.target.value)} 
                 className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
               />
               <datalist id="entry-strategy-options">
                  {entryStrategies.map((strategy) => <option key={strategy} value={strategy} />)}
               </datalist>
             </div>
             )}
          </div>
        </div>

        {/* Exit Group */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label>
          <div className="space-y-4">
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
               <div className="relative flex items-center">
                  <input 
                    type="time" 
                    ref={exitTimeRef}
                    value={to24HourTime(journal["Exit Time"])}
                    onChange={e => setJournal(prev => ({ ...prev, "Exit Time": e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
                  />
                  <FaRegClock
                    className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                    onClick={() => exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()}
                  />
               </div>
             </div>

             {!settings["Hide Exit Price"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
               <input 
                 type="number" 
                 placeholder="0.00000" 
                 value={journal["Exit Price"] ?? ""}
                 onChange={e => handleJournalChange("Exit Price", e.target.value)}
                 className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
               />
             </div>
             )}

             {!settings["Hide Exit Strategy"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
               <input 
                 type="text" 
                 list="exit-strategy-options"
                 value={journal["Exit Strategy"]} 
                 onChange={e => handleJournalChange("Exit Strategy", e.target.value)} 
                 className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
               />
               <datalist id="exit-strategy-options">
                  {exitStrategies.map((strategy) => <option key={strategy} value={strategy} />)}
               </datalist>
             </div>
             )}
          </div>
        </div>

        {/* Calculated Stats Group */}
                 <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                    <div className="space-y-6">
                      <div>
                        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
                          Actual Lot Size<span className="text-red-500 text-lg">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={
                            journal["Size"] !== undefined && journal["Size"] !== ""
                              ? journal["Size"]
                              : calculatedSummary?.lots ?? ""
                          }
                          onChange={e => handleJournalChange("Size", e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                          placeholder="0.00"
                        />
                      </div>
                      
                      {/* NEW: Potential Pips Field (Note Removed) */}
                      {!settings["Hide Potential"] && (
                      <div>
                        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                          Potential Pips
                          <Tooltip
                            variant="blue"
                            backdrop
                            content={
                              <div className="space-y-2">
                                <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                                  Potential Pips
                                </div>
                                <p className="text-[14px] leading-relaxed">
                                  The total number of pips that were available in the move.
                                </p>
                              </div>
                            }
                          >
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                              ?
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          id="potentialPips"
                          type="number"
                          value={journal["Potential"] ?? ""}
                          onChange={(e) => handleJournalChange("Potential", e.target.value)}
                          className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                          placeholder="Qty"
                        />
                      </div>
                        )}
                    </div>

            {!settings["Hide Ideal Stop Loss"] && (
            <div>
              <label className="block font-bold mb-1 mt-4 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                              Ideal Stop Loss
                              <Tooltip
                                variant="blue"
                                backdrop
                                content={
                                  <div className="space-y-2">
                                    <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                                      Ideal Stop Loss
                                    </div>
                                    <p className="text-[14px] leading-relaxed">
                                      You should always use your strategy for setting the proper stop loss. You may notice that the stop loss is too big or too small.
                                    </p>
                                    <p className="text-[14px] leading-relaxed">
                                      You can use this field to help track a more ideal stop loss for the trade and see the results on the Stats page.
                                    </p>
                                  </div>
                                }
                              >
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                                  ?
                                </span>
                              </Tooltip>
                            </label>
              <input
                id="ideal"
                type="number"
                value={journal.IdealSL}
                onChange={(e) =>
                  setJournal(prev => ({ ...prev, IdealSL: e.target.value }))}
                className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                placeholder="Qty"
              />
            </div>
            )}
         </div>

      </div>
    </div>

    {/* SECTION 3: OUTCOME */}
    <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold mb-4 border-l-4 border-green-500 pl-3">
        3. Outcome
      </h4>

      {/* 3a. Win/Loss Buttons */}
      <div className="mb-6">
        <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
          Result{!isDidNotTake && <span className="text-red-500 text-lg">*</span>}
        </label>
        <div className="flex gap-4 max-w-md">
          <button
            type="button"
            onClick={() => setJournal(j => ({ ...j, Win: true, Loss: false }))}
            disabled={isDidNotTake}
            className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
              journal["Win"] 
                ? "bg-green-500 text-white border-green-600 ring-2 ring-green-300 dark:ring-green-800" 
                : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-gray-600 disabled:opacity-50"
            }`}
          >
            WON
          </button>
          <button
            type="button"
            onClick={() => setJournal(j => ({ ...j, Win: false, Loss: true }))}
            disabled={isDidNotTake}
            className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
              journal["Loss"] 
                ? "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-900" 
                : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 disabled:opacity-50"
            }`}
          >
            LOSS
          </button>
        </div>
      </div>

      {/* 3b. Backtesting Instructions */}
      <div className="border border-yellow-300 dark:border-yellow-600 rounded-xl p-4 mb-6 bg-yellow-50 dark:bg-yellow-900/10">
        <div className="text-yellow-800 dark:text-yellow-200 mb-2 text-sm font-semibold flex items-start gap-2">
          <span className="mt-1">💡</span>
          <div>
            Enter <strong>New Balance</strong> OR <strong># of Pips</strong>.
            <p className="font-normal mt-1 opacity-90">
              Entering Pips will auto-calculate the New Balance based on your lot size. 
              If you enter a New Balance, the Pips field will clear.
            </p>
          </div>
        </div>
      </div>

      {/* 3c. Balance / Pips Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
        
        {/* New Balance */}
        <div>
           <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">New Account Balance</label>
           <div className="relative">
             <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
             <input
                type="text"
                inputMode="decimal"
                value={journal["Actual Balance"] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Clear Pips when Actual Balance is entered
                  setJournal((j) => ({
                    ...j,
                    ["Actual Balance"]: raw,
                    ["Pips"]: raw ? "" : j["Pips"],
                  }));
                }}
                 onBlur={(e) => {
                   const raw = String(e.target.value ?? "").trim();
                   setJournal((j) => ({
                     ...j,
                     // keep empty when blank OR when Pips is populated
                     ["Actual Balance"]: (raw === "" || String(j["Pips"] ?? "").trim() !== "")
                       ? ""
                       : parseCurrency(raw),
                   }));
                 }}
                className={`w-full pl-8 px-4 py-3 rounded-xl border focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
                  missingFields.includes("- Actual Balance or Pips")
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-500"
                }`}
                placeholder="0.00"
              />
           </div>
        </div>

        {/* Pips */}
        <div>
          <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
             Pips Captured/Lost
          </label>
          <input
            id="pips"
            type="number"
            value={journal["Pips"] || ""}
            onChange={(e) => {
              const val = e.target.value;
              // Clear Actual Balance when Pips are entered
              setJournal((j) => ({
                ...j,
                ["Pips"]: val,
                ["Actual Balance"]: val ? "" : j["Actual Balance"],
              }));
            }}
            className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-yellow-400 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
              missingFields.includes("- Actual Balance or Pips")
                ? "border-red-500"
                : "border-gray-300 dark:border-gray-500"
            }`}
            placeholder="Qty"
            inputMode="numeric"
          />
        </div>

        {/* NEW: Target Reached Checkbox */}
              <div className="mt-6 flex items-center bg-green-50 dark:bg-green-900/10 p-3 rounded-xl border border-green-100 dark:border-green-800">
                 <input 
                     type="checkbox" 
                     id="targetReachedFut" 
                     checked={!!journal["Target Reached"]} 
                     onChange={e => handleJournalChange("Target Reached", e.target.checked)} 
                     className="mr-3 w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer" 
                 />
                 <label htmlFor="targetReachedFut" className="text-sm font-bold text-green-700 dark:text-green-400 cursor-pointer">
                     Target Reached?
                 </label>
              </div>

      </div>
    </div>

    {/* SECTION 4: PSYCHOLOGY & REVIEW */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">
        4. Psychology & Review
      </h4>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        {/* Rules */}
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?</label>
            <select 
              value={journal["Did I follow my Rules?"]} 
              onChange={e => handleJournalChange("Did I follow my Rules?", e.target.value)} 
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select</option>
              {RULES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        {/* Mood */}
        {!settings["Hide Mood"] && (
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label>
            <select 
              value={journal["Mood_Mindset"]} 
              onChange={e => handleJournalChange("Mood_Mindset", e.target.value)} 
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select (1=Calm, 5=Stress)</option>
              {MOOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        )}

        {/* Grade */}
        {!settings["Hide Grade"] && (
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label>
            <select 
              value={journal["Grade"]} 
              onChange={e => handleJournalChange("Grade", e.target.value)} 
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select</option>
              {GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        )}
      </div>

      {/* Inputs */}
      <div className="space-y-6">
         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label>
           <input 
             type="text" 
             value={journal["Links_Screenshots"]} 
             onChange={e => handleJournalChange("Links_Screenshots", e.target.value)} 
             placeholder="Paste URL here..."
             className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
           />
         </div>
         
         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label>
           <textarea 
             rows={3} 
             value={journal["Notes"]} 
             onChange={e => handleJournalChange("Notes", e.target.value)} 
             placeholder="Anything to note about this trade?"
             className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" 
           />
         </div>

         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
             Emotional Notes
           </label>
           <textarea 
             rows={3} 
             value={journal["Emotional Notes"] || ""} 
             onChange={e => handleJournalChange("Emotional Notes", e.target.value)} 
             placeholder="How were you feeling during this execution? Anxious? Confident? Rushed?"
             className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" 
           />
         </div>

        {!settings["Hide Lesson"] && (
         <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
            <label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">
              Lesson (Optional)
            </label>
            <textarea 
              rows={2} 
              value={journal["Lesson"]} 
              onChange={e => handleJournalChange("Lesson", e.target.value)} 
              placeholder="What did you learn from this trade?"
              className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white bg-white transition resize-y" 
            />
            <div className="mt-3">
              <label className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  checked={journal["LogLesson"]} 
                  onChange={e => handleJournalChange("LogLesson", e.target.checked)} 
                  className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
                />
                Save to Lessons Database
              </label>
            </div>
         </div>
         )}
      </div>
    </div>
  
  </div>

  {/* Save Button */}
  <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700">
     <button 
       className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-600 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
       onClick={handleSaveTrade} 
       disabled={savingTrade}
     >
        {savingTrade ? "Saving..." : "Save Trade"}
     </button>
  </div>
</div>
)} {/* <--- ADD THIS CLOSING BRACE/PARENTHESIS HERE */}

      {/* === Overlays & Popups (now INSIDE component render) === */}

       {projectionOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
    <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b]
      max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
      <button
        className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800"
        onClick={() => setProjectionOpen(false)}
        aria-label="Close"
      >×</button>
        
        <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
            <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
            <path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Win Projection Table
        </h3>

        {/* --- EXPLANATORY TEXT (Forex Adapted) --- */}
        <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          Projections show how many trades (using at least your minimum lot size setting) it would take to reach your goal. Any trades with the red background indicate when you are over your risk tolerance. The totals are estimates and don't take into account broker fees and commissions.
          <br /><br />
          If you prefer to always trade the same lot size, you can set the minimum and maximum lot size to the same number in the settings.
        </p>

        {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
        <div className="mb-4 flex items-center justify-end">
          <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
            <span className="mr-3">Group by Trading Day</span>
            <div className="relative flex items-center">
              <input 
                type="checkbox" 
                className="sr-only" 
                checked={showDaySeparators} 
                onChange={() => setShowDaySeparators(!showDaySeparators)} 
              />
              <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
              <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
            </div>
          </label>
        </div>
        {/* 👆 END TOGGLE BLOCK 👆 */}

        {/* --- DYNAMIC TIME ESTIMATION BLOCK --- */}
        {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const trades      = winProjection?.trades || [];
              const tradeCount = winProjection?.tradeCount || 0;

              // 1. Check if account is blown (Prop Firm Max Loss or $0)
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const isProp = (init > 0 && maxLoss > 0);
              const ruinThreshold = isProp ? Math.max(0, init - maxLoss) : 0;

              if (running <= ruinThreshold) {
                return (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm font-bold text-red-800 dark:text-red-200">
                    Your account balance has reached your maximum loss limit or $0. Switch to your demo account to review your plan.
                  </div>
                );
              }

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal, settings["Account Currency"])}.
                  </div>
                );
              }

          // 2. Calculate "Trading Days" using the Hybrid Logic
          let estimatedDays = 0;
          let currentDayAccumulator = 0;

          if (dailyGoal > 0) {
            for (const t of trades) {
              const profit = t.tradeProfit || 0;
              
              if (profit >= dailyGoal) {
                estimatedDays++;
                currentDayAccumulator = 0; 
              } else {
                currentDayAccumulator += profit;
                if (currentDayAccumulator >= dailyGoal) {
                  estimatedDays++;
                  currentDayAccumulator = 0;
                }
              }
            }
            if (currentDayAccumulator > 0) estimatedDays++;
          } else {
            estimatedDays = tradeCount;
          }

          return (
            <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <FaRegClock className="text-blue-500" />
                Estimated Time to Goal
              </h4>
              
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex-1">
                   <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                    {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">winning trades in ≈ </span> {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span> 
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                    To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades in about {estimatedDays} if you reached your trading daily goal.
                  </p>
                </div>

                <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                  <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>While trade profit &lt; <strong>{formatCurrency(dailyGoal, settings["Account Currency"])}</strong>, we assume you trade until you hit your daily goal.</li>
                    <li>Once trade profit &ge; <strong>{formatCurrency(dailyGoal, settings["Account Currency"])}</strong>, we assume 1 trade per day.</li>
                    <li>As your balance compounds, your position sizes and profits will <strong>increase</strong>. Make sure to update your <strong>Daily Goal</strong> in the settings to reflect your growing account size!</li>
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}

        {winProjection?.tradeCount === 0 && (
            <div className={`text-center py-4 font-semibold ${
              (() => {
                const r = Number(settings["Running Balance"] || 0);
                const i = Number(settings["Initial Balance"] || 0);
                const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : 0;
                return r <= thresh;
              })() 
                ? "text-red-600 dark:text-red-400" 
                : "text-green-600 dark:text-green-300"
            }`}>
              {(() => {
                const r = Number(settings["Running Balance"] || 0);
                const i = Number(settings["Initial Balance"] || 0);
                const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : 0;
                if (r <= thresh) {
                  return "No trades can be taken. Your account is blown. Reset your journal or create a new account and refine your strategy.";
                }
                return "No trades can be taken with the current settings. If you have reached your goal you can adjust it and continue trading.";
              })()}
            </div>
          )}

      {winProjection?.tradeCount > 0 && (() => {
              const r = Number(settings["Running Balance"] || 0);
              const i = Number(settings["Initial Balance"] || 0);
              const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : 0;
              return r > thresh; 
            })() && (
      <div className="overflow-auto max-h-[50vh]">
        <table className="min-w-full border-separate border-spacing-0 rounded-xl shadow bg-white/95 dark:bg-gray-900/90 border-2 border-blue-200 dark:border-blue-800">
          <thead>
            <tr className="bg-blue-100 dark:bg-blue-900/80">
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Max Drawdown</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Lot Size</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Risk</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade Profit</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let runningBal = Number(settings["Running Balance"] || 0); 
              const rowsToShow = isTrialing ? winProjection.trades.slice(0, 10) : winProjection.trades;

              let currentDay = 1;
              let dayAccumulator = 0;
              const dailyGoalAmt = Number(settings["Daily Goal"] || 0);

              const tradesWithDays = rowsToShow.map((row) => {
                const assignedDay = currentDay;
                if (dailyGoalAmt > 0) {
                  dayAccumulator += (row.tradeProfit || 0);
                  if (dayAccumulator >= dailyGoalAmt) {
                    currentDay++;
                    dayAccumulator = 0;
                  }
                }
                return { ...row, day: assignedDay };
              });

              return tradesWithDays.map((row, i) => {
                const before = runningBal;                  
                const after  = before + (row.tradeProfit ?? 0); 
                runningBal = after;                         

                const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDays[i-1].day;

                return (
                  <React.Fragment key={i}>
                    {isNewDay && (
                      <tr className="bg-blue-100/50 dark:bg-blue-900/30">
                        <td colSpan="7" className="px-3 py-2 text-center text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-widest border-y border-blue-200 dark:border-blue-800">
                          — Day {row.day} —
                        </td>
                      </tr>
                    )}
                    
                    <tr className={`${row.underfunded ? "bg-red-100 dark:bg-red-900/80" : "hover:bg-blue-50 dark:hover:bg-blue-800/80"} transition border-b border-gray-100 dark:border-gray-800`}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(before, settings["Account Currency"])}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.riskDollar ?? (row.lots * Number(settings["Stop Loss"] || 0) * Number(calculatedSummary?.pipValue || 0)), settings["Account Currency"])}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{Number(row.lots ?? 0).toFixed(2)}</td>
                      <td className={`px-3 py-2 ${row.underfunded ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>
                        {formatCurrency(row.totalRisk ?? 0, settings["Account Currency"])}
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.tradeProfit ?? 0, settings["Account Currency"])}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(after, settings["Account Currency"])}</td>
                    </tr>
                  </React.Fragment>
                );
              });
            })()}
          </tbody>
        </table>
            {/* --- NEW: Trial Indicator --- */}
                    {isTrialing && winProjection.trades.length > 5 && (
                      <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                        <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                          Full projection table available after Free Trial
                        </p>
                      </div>
                    )}
                  </div>
                  )}
          </div>
        </div>
      )}

     {lossProjectionOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
    <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616]
      max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
      <button
        className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800"
        onClick={() => setLossProjectionOpen(false)}
        aria-label="Close"
      >×</button>

      <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
          <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" />
          <path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Loss Projection Table
      </h3>

      <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
        Projections show how many consecutive full-loss trades (using at least your minimum lot size setting) it would take to blow your account. The totals are estimates and do not include fees.
        <br /><br />
        If you prefer to always trade the same lot size, you can set the minimum and maximum lot size to the same number in the settings.
      </p>

      {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
      <div className="mb-4 flex items-center justify-end">
        <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
          <span className="mr-3">Group by Trading Day</span>
          <div className="relative flex items-center">
            <input 
              type="checkbox" 
              className="sr-only" 
              checked={showDaySeparators} 
              onChange={() => setShowDaySeparators(!showDaySeparators)} 
            />
            <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
            <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
          </div>
        </label>
      </div>

      {/* --- DYNAMIC SUMMARY BLOCK --- */}
      {(() => {
        const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
        const init      = Number(settings["Initial Balance"] || 0);
        const maxLoss   = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
        const running   = Number(settings["Running Balance"] || 0);
        const tradeCount = lossProjection?.tradeCount || 0;

        if (!(dailyLoss > 0)) return null;

        let daysToRuin = 0;
        let ruinThreshold = 0;
        let isProp = (init > 0 && maxLoss > 0);

        if (isProp) {
          ruinThreshold = Math.max(0, init - maxLoss);
          const distance = Math.max(0, running - ruinThreshold);
          daysToRuin = Math.ceil(distance / dailyLoss);
        } else {
          daysToRuin = Math.ceil(running / dailyLoss);
        }

        return (
          <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
            <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
              Risk to Ruin Analysis
            </h4>
            
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="flex-1">
                <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                  {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                  To reach your failure threshold of <strong>{formatCurrency(ruinThreshold, settings["Account Currency"])}</strong> based on your current risk settings.
                </p>
              </div>

              <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                </div>
                <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                  If you hit your Daily Loss Limit ({formatCurrency(dailyLoss, settings["Account Currency"])}) every single day.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-red-200 dark:border-red-800 shadow">
        <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
          <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Trade #</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance Before</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Lot Size</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Loss Amount</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance After</th>
            </tr>
          </thead>
          <tbody>
            {(lossProjection?.trades?.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                  No trades can be taken with the current settings.
                </td>
              </tr>
            ) : (
              (() => {
                const rowsToShow = isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades;
                
                let currentLossDay = 1;
                let lossAccumulator = 0;
                const dailyLossAmt = Number(settings["Daily Loss Limit"] || 0);

                const tradesWithDaysLoss = rowsToShow.map(row => {
                  const assignedDay = currentLossDay;
                  if (dailyLossAmt > 0) {
                    lossAccumulator += (row.loss || 0);
                    if (lossAccumulator >= dailyLossAmt) {
                      currentLossDay++;
                      lossAccumulator = 0; 
                    }
                  }
                  return { ...row, day: assignedDay };
                });

                return tradesWithDaysLoss.map((row, i) => {
                  const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDaysLoss[i-1].day;

                  return (
                    <React.Fragment key={i}>
                      {isNewDay && (
                        <tr className="bg-red-100/50 dark:bg-red-900/30">
                          <td colSpan="5" className="px-4 py-2 text-center text-xs font-bold text-red-800 dark:text-red-300 uppercase tracking-widest border-y border-red-200 dark:border-red-800">
                            — Day {row.day} —
                          </td>
                        </tr>
                      )}
                      
                      <tr className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.balanceBefore, settings["Account Currency"])}
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {Number(row.lots ?? 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">
                          {formatCurrency(row.loss, settings["Account Currency"])}
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.balanceAfter, settings["Account Currency"])}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                });
              })()
            )}
          </tbody>
        </table>
              {/* --- NEW: Trial Indicator --- */}
              {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    Full projection table available after Free Trial
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      

      {showRiskWarningPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={() => setShowRiskWarningPopup(false)} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
                    </svg>
              High Risk Warning!
            </h2>

            <p className="mb-3 text-gray-800 dark:text-gray-100">
              <span className="font-bold">Based on your Balance, Stop Loss, Risk and Minimum Lot Size, the </span>
              <span className="font-bold text-red-600">Total Risk</span> on this trade (
              <span className="font-bold text-red-600">{formatCurrency(calculatedSummary?.totalRisk, settings["Account Currency"])}</span>) is higher than your risk allowance (
              <span className="font-bold text-red-600">{formatCurrency(calculatedSummary?.percentOfBalance, settings["Account Currency"])}</span>)!
            </p>


            {maxStopLossValue > 0 && (
              <p className="mb-3 text-gray-800 dark:text-gray-100">
                <span className="font-semibold">Maximum Stop Loss </span> size to stay within your risk:{" "}
                <span className="font-bold text-red-600">{maxStopLossValue}</span> pips.
              </p>
            )}

            {Number(calculatedSummary?.minLot ?? settings["Min Lot Size"]) > 0 && (
              <p className="mb-3 text-gray-800 dark:text-gray-100">
                <span className="font-semibold">Minimum Lot Size</span>:{" "}
                <span className="font-bold text-red-600">
                  {Number((calculatedSummary?.minLot ?? settings["Min Lot Size"]) ?? 0.01).toFixed(2)}
                </span>
              </p>
            )}

            <p className="mb-4  font-semibold text-gray-700 dark:text-gray-300">Consider a different symbol or wait for another trade setup that requires a smaller stop loss to stay within your risk management rules.</p>
            <button onClick={() => setShowRiskWarningPopup(false)} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}




            {showTradeSaved && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
                  <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
                    <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48">
                      <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/>
                      <path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  </span>
                  <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">Trade Saved!</div>
                  <div className="text-gray-700 dark:text-gray-200 mb-1 text-center">Your journal entry has been saved.</div>
                  
                  {/* ✅ CHANGE THE onClick HANDLER HERE */}
                  <button 
                    className="mt-2 px-5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition" 
                    onClick={handleCloseSaveConfirmation}
                  >
                    OK
                  </button>
                </div>
              </div>
            )}


      {currentPopup?.id === 'dailyGoalPopup' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          {/* Confetti for celebration! */}
          <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>

            <h2 className="text-2xl font-bold mb-4 text-green-700 dark:text-green-500 flex items-center">
              <svg className="w-7 h-7 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Congratulations!
            </h2>

            <div className="mb-4 text-lg text-gray-800 dark:text-gray-100 font-semibold text-center">
              You reached your{" "}
              <span className="text-green-600 dark:text-green-300 font-bold">Daily Goal</span>!
              <br />
              {currentPopup.data?.dailyGoal !== undefined && (
                <span className="block mt-2 text-base text-green-700 dark:text-green-500 font-bold">
                  Goal:{" "}
                  <span className="text-green-700 dark:text-green-500">
                    {Number(currentPopup.data.dailyGoal).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </span>
              )}

              <span className="block mt-2 font-bold text-lg text-gray-700 dark:text-gray-200">
                Take a moment to celebrate your discipline and progress.<br /><br />
                Size down if you continue to trade, you don't want to risk giving your earnings back to the market.<br /><br />
                {/* Dynamic day-based message (local time). Pass a TZ if you prefer: getDailyGoalMessage('America/New_York') */}
                {getDailyGoalMessage()}
              </span>
            </div>

            <button
              onClick={closeCurrentPopup}
              className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === "dailyLossLimitExceeded" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => {
          closeCurrentPopup();
        }}
        aria-label="Close"
      >
        ×
      </button>

      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
        </svg>
        Daily Loss Limit Reached
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You are down <span className="font-bold text-red-600">
          {formatCurrency(currentPopup?.data?.lostBeyondWins || 0, settings["Account Currency"])}</span> beyond today’s gains.
      </p>

      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Your daily loss limit is <span className="font-bold">
          {formatCurrency(currentPopup?.data?.limit || 0, settings["Account Currency"])}</span>.
               </p>
                 <p className="mb-4 text-gray-700 dark:text-gray-300">
           Consider stopping for the day and come back tomorrow.  The key is avoiding going on tilt and revenge trading.
          </p>

      <button
        onClick={() => {
          closeCurrentPopup();
        }}
        className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'maxLossLimitBlown' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
        <div className="flex items-center mb-4">

          <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
          </svg>

          <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
            Account Blown
          </h2>
          
        </div>
      <p className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        Your balance has reached <span className="font-bold text-red-600">$0</span>. <br/><br/>This is a good time to switch to your demo account to review and refine your plan before depositing more funds.
      </p>
      <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">
        The key is to preserve your capital.
      </p>
        <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">
        Spend time backtesting until you've mastered your strategy and are consistently profitable. This is the best way to prepare before investing more of your hard-earned money.
      </p>
      {/* Add this just above the close button area in maxLossLimitBlown */}
      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-100">
       Trading is 80% Psychology.  Review mindset and risk-management refreshers, the Psychology of Profit has some really good videos.
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/@Psychology-Of-Profit" // <- replace with the exact channel if different
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-red-600 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition"
        >
          {/* Play icon */}
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Visit the Psychology of Profit Youtube Channel
          <span className="sr-only">(opens in a new tab)</span>
        </a>

             </div>

      <p className="font-bold mt-4 mb-4 text-red-800 dark:text-red-400">
        Don't give up. You can do this!  
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



 {currentPopup?.id === 'overtradingPopup' && (
   <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
     <div
       className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn"
       role="dialog"
       aria-modal="true"
       aria-labelledby="overtrading-title"
       aria-describedby="overtrading-desc"
     >
       <button
         className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
         onClick={closeCurrentPopup}
         aria-label="Close"
       >
         ×
       </button>

       <h2
         id="overtrading-title"
         className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center"
       >
         <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
           <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
         </svg>
         Warning: Overtrading!
       </h2>

       <p className="mb-4 text-gray-800 dark:text-gray-100">
         <span className="font-bold text-yellow-700 dark:text-yellow-300">You have logged more trades than your daily plan allows.</span>
       </p>

       <p className="mb-4 text-gray-800 dark:text-gray-100">
         <span className="font-bold">Your maximum trades per day is: </span>
         <span className="font-bold text-yellow-700 dark:text-yellow-300">
           {currentPopup.data.maxTradesPerDay}
         </span>
       </p>

       <p id="overtrading-desc" className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
         Overtrading can lead to impulsive decisions and unnecessary risk by draining your mental capital.
       </p>

       <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
         Review your trades, lock your account, and come back tomorrow recharged for a strong start!
       </p>

       <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
         Below is a really good video from Psychology of Profits regarding protecting your mental capital.
       </p>

       {/* Actions */}
       <div className="mt-6 flex flex-wrap gap-3 justify-end">
         <a
           href="https://www.youtube.com/watch?v=6vZG4epFEOM"
           target="_blank"
           rel="noopener noreferrer"
           className="inline-flex items-center gap-2 border border-yellow-500 text-yellow-700 dark:text-yellow-300 font-bold px-4 py-2 rounded hover:bg-yellow-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition"
         >
           {/* Play icon */}
           <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
             <path d="M6 4l10 6-10 6V4z" />
           </svg>
           Watch: Mental Capital
           <span className="sr-only">(opens in a new tab)</span>
         </a>

         <button
           onClick={closeCurrentPopup}
           className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
         >
           Close
         </button>
       </div>
     </div>
   </div>

 )}

     {currentPopup?.id === 'tooManyLossesPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Daily Lost Trades Limit Reached!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          You've hit your max daily loss count.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum losses per day is: </span>
        <span className="font-bold text-red-700 dark:text-red-300">
          {currentPopup.data.maxLossesPerDay}
        </span>
      </p>
      <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">
        Consider stopping for the day to protect your capital. <br/> <br/>Review your trades and reset for tomorrow.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

      {currentPopup?.id === 'lossLimitWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Daily Loss Limit Exceeded!
            </h2>
            <p className="mb-3  font-semibold text-lg text-gray-800 dark:text-gray-100">
              Your total loss for today is <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossForDay)}</span>,
              which exceeds your daily loss limit of <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossLimitAmount)}</span>.
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-400">
              Please consider stopping trading for the day to protect your account.
            </p>
            <p className="mb-6 font-semibold text-red-700 dark:text-red-400">
              You are worthy of success!  Don't give up!
            </p>
            <button
              onClick={closeCurrentPopup}
              className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}


      {currentPopup?.id === "win10NoRulesPopup" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
              </svg>
              Warning - Big Wins Without Following Rules
            </h2>
            <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
              {currentPopup.data?.message
              ?.split('\n')
              .map((line, i) =>
                line.trim() === "Discipline > excitement!" ? (
                  <span key={i} className="font-bold text-blue-700 dark:text-blue-500">
                    Discipline &gt; excitement!
                    <br />
                  </span>
                ) : (
                  <React.Fragment key={i}>
                    {renderWithTiltTooltip(line)}
                    <br />
                  </React.Fragment>
                  )
                )}
            </div>
            <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === "win10WithRulesPopup" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Congratulations – Following Your Rules!
            </h2>
            <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 whitespace-pre-line">
             {(currentPopup.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.")
               .split('\n')
               .map((line, i) => (
                 <React.Fragment key={i}>
                   {formatThreeLossMessage(line)}
                   <br />
                 </React.Fragment>
               ))}
            </div>
            <button
              onClick={closeCurrentPopup}
              className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'moodVolatilityPopup' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
              </svg>
              Mood Volatility Warning
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-blue-700 dark:text-blue-300">
                Your emotional state appears to be heightened.
              </span>
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
              Trading with intense emotions—whether it's excitement or stress—can lead to decisions based on feeling, not strategy. Your greatest asset is a calm and objective mind.
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
              Before your next trade, journal what you're feeling and why. We recommend you pause until your focus is back on your process.
            </p>
             <p className="font-bold mb-6 text-gray-700 dark:text-gray-300">
              If you consistently trade in a heightened emotional state, you may be using a position size, symbol, or timeframe that doesn't fit your personality.
            </p>
            <button
              onClick={closeCurrentPopup}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'twoLossAfterGoalWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
              </svg>
              Warning – Losses After Goal
            </h2>
            <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line text-left overflow-visible">
                {(currentPopup.data?.message || "You had 2 losses after reaching your daily goal.")
                  .split('\n')
                  .map((line, i) => (
                    <React.Fragment key={i}>
                      {renderWithTiltTooltip(line)}
                      <br />
                    </React.Fragment>
                ))}
            </div>
            <button
              onClick={closeCurrentPopup}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              OK
            </button>
          </div>
        </div>
      )}


{currentPopup?.id === 'overRiskWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
              </svg>
              Over Risk Warning!
            </h2>
            
            {/* ✅ Updated message format */}
            <div className="text-gray-800 dark:text-gray-100 text-left space-y-4">
              <p>You lost more than your planned risk amount.</p>
              
              <div className="pl-4">
                <p>Risk amount: <span className="font-bold text-yellow-500 dark:text-yellow-400">{formatCurrency(currentPopup.data.allowedRisk)}</span></p>
                <p>Amount lost: <span className="font-bold text-yellow-500 dark:text-yellow-400">{formatCurrency(currentPopup.data.lossForTrade)}</span></p>
              </div>

              <p className="font-semibold text-gray-600 dark:text-gray-300">
                Make sure that you are always using the proper stop loss and not moving it. If the trade is going too far against your analysis, you can always get out and get back in at the right time.
              </p>
              
              <p className="font-bold text-yellow-500 dark:text-yellow-400">
                Keep your losers small and your winners big and never marry a trade idea!
              </p>
            </div>

            <button
              onClick={closeCurrentPopup}
              className="mt-6 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

{currentPopup?.id === 'drawdownWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Drawdown Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-purple-700 dark:text-purple-300">
          Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Drawdown amount: </span>
        <span className="font-bold text-purple-700 dark:text-purple-300">
          ${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}
        </span>
        <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">
          ({currentPopup.data.drawdownPct?.toFixed(2)}%)
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider pausing to review your trades and protect your capital before proceeding.
      </p>
      <p className="font-bold mt-6 mb-6 text-gray-700 dark:text-gray-300">
        No one wins 100% of the time and there is nothing wrong with switching to demo until you are winning consistently and growing your account.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'consecutiveWinsPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M8 12l2 2l4-4" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Hot Streak!
      </h2>
      <p className="mb-3 text-gray-800 dark:text-gray-100">
             <span className="font-bold text-blue-700 dark:text-blue-300">You've won 5 trades in a row!</span>
           </p>
           <p className="text-gray-700 font-semibold dark:text-gray-300">
             Stay disciplined—don&apos;t increase your risk or change your plan just because you&apos;re on a hot streak.
           </p>
           <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-blue-300">
             Stick to your process and keep your edge!
           </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


      {currentPopup?.id === "accountGoalReached" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7" strokeWidth="2" />
              </svg>
              Account Goal Achieved!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-green-700 dark:text-green-300">Congratulations! You've reached your account goal!</span>
            </p>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Your balance is <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span>.
              <br />
              Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span>
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-green-700 dark:text-green-300">Your hard work has paid off!</span>
            </p>
            <button onClick={closeCurrentPopup} className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'lessonsLearnedStreakWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 6v6l4 2" strokeWidth="2" />
              </svg>
              Lessons Learned Streak!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-blue-700 dark:text-blue-300">Great job extracting value from your trades!</span>
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consistently recording your lessons learned will accelerate your growth. <br/><br/> Review them every day during your pretrade routine until they become seared in your brain!</p>
            <button
              onClick={closeCurrentPopup}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

{currentPopup?.id === 'riskOfRuinWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
        </svg>
        Risk of Ruin Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          You have lost at least 75% of your initial balance. Based on your current risk, your account could be wiped out in just <span className="underline">{lossProjection.tradeCount}</span> consecutive losses.  
        </span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Consider lowering your risk per trade to protect your remaining capital.
      </p>
      <p className="mt-6 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Switch to your demo account and trade there until you are winning consistently. 
      </p>
      <p className="mt-6 mb-6 font-semibold text-blue-700 dark:text-blue-400">
        Don't be discouraged, you can do this!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

      {currentPopup?.id === "twoLossWarning" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
              </svg>
              Warning - 2 Consecutive Losses!
            </h2>
           <div className="mb-4 text-gray-800 font-semibold dark:text-gray-100 text-left">
             {currentPopup.data?.message
               ? currentPopup.data.message.split('\n').map((line, i) => (
                   <React.Fragment key={i}>
                     {highlightHundredPercent(line)}
                     <br />
                   </React.Fragment>
                 ))
               : (
                 <>
                   You've lost <span className="font-bold text-red-500">2 trades in a row</span>.<br />
                   Consider taking a break and reviewing your trades and strategy before continuing.<br />
                   You don't want the risk of going on tilt.
                 </>
               )
             }
           </div>
            <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'threeLossWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="three-loss-title"
            aria-describedby="three-loss-desc"
          >
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >
              ×
            </button>

            <h2
              id="three-loss-title"
              className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center"
            >
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Danger!
            </h2>

            <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
              {currentPopup.data?.message.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {formatThreeLossMessage(line)}
                  <br />
                </React.Fragment>
              ))}

              <div className="mt-4 text-blue-400 font-bold">
                {"You got this!!"}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              {currentPopup.data?.videoLink && (
                <a
                  href={currentPopup.data.videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition"
                >
                  {/* Play icon */}
                  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M6 4l10 6-10 6V4z" />
                  </svg>
                  Watch: Dealing with Trading Losses
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              )}

              <button
                onClick={closeCurrentPopup}
                className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Required Fields */}
      {missingFields.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={() => setMissingFields([])} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
              </svg>
              Required Fields Missing
            </h2>
            <ul className="mb-4 text-gray-800 dark:text-gray-200">
              {missingFields.map(f => (
                <li key={f} className="pl-2">
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={() => setMissingFields([])} className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow">
              Close
            </button>
          </div>
        </div>
      )}



      {/* --- ADD THIS FLOATING ACTION BUTTON AND CALCULATOR --- */}
          <button
            onClick={() => setShowCalculator(true)}
            title="Open Calculator"
            className="fixed bottom-12 right-6 z-40 bg-gray-800 dark:bg-gray-700 text-white rounded-full p-4 shadow-lg hover:bg-gray-900 dark:hover:bg-gray-600 transition-transform transform hover:scale-110"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </button>

{/* Strategy Checklist Floating Panel */}
      {showStrategyModal && (
        <div className="fixed top-24 right-8 z-40 animate-fadeIn pointer-events-auto w-96 max-w-[calc(100vw-2rem)] shadow-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border-2 border-blue-500 relative max-h-[80vh] flex flex-col">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setShowStrategyModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2 shrink-0">
              <FaClipboardList className="text-blue-500" />
              Strategy Checklist
            </h2>
            
            <div className="overflow-y-auto mb-4 pr-2 flex-1">
              {userPrefs?.tradingRules ? (
                <div className="space-y-3">
                  {userPrefs.tradingRules.split('\n').filter(rule => rule.trim() !== '').map((rule, index) => (
                    <label key={index} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition">
                      <input 
                        type="checkbox" 
                        className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" 
                      />
                      <span className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed text-sm">
                        {rule}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="mb-2">No Trading Rules defined yet.</p>
                  <p className="text-sm">Head over to the Lessons page to define your core strategy.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
              <button
                onClick={() => setShowStrategyModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-xl shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

          
          {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
          {/* --- END OF NEW CODE --- */}
      </div>
  );
}